// Attendance.tsx
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
// The face check sits above Clock In; everything below this line behaves the
// same whether or not it is switched on.
import FaceCheckModal from "../components/attendance/FaceCheckModal";
import {
  getFaceStatus,
  readFaceFailure,
  revokeFaceConsent,
  type FaceStatus,
} from "../api/face";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Calendar,
  Download,
  Eye,
  User,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  Users,
  Activity,
  Filter,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Briefcase,
  Menu,
  Pencil,
  Trash2,
  Plus,
  CalendarDays
} from "lucide-react";
import {
  getAttendanceDayWise,
  getAttendanceSummary,
  getAttendanceStatus,
  exportAttendanceToCSV,
  adminMarkPresent,
  adminMarkAbsent,
  adminUpdateWorkingHours,
  adminDeleteAttendancePunch,
  sendWebClockEvent,
  geolocationBlockedByInsecureOrigin,
  getMyWorkMode,
  getAttendanceByDate,
  AttendanceWorkingHour,
  AttendanceStatus,
  AttendanceSession,
  TodayAttendance,
  PieData,
  cancelEarlyExitRequest,
  listEarlyExitRequests,
  ATTENDANCE_MODE_LABELS,
  formatPunchPlace,
  punchPlaceMapUrl,
  type AttendanceMode,
  type EarlyExitPrompt,
  type EarlyExitRequestRow,
} from "../api/attendance";
import { LogIn, LogOut, MapPin, Home, Building2, ScanFace, CalendarClock } from "lucide-react";
import { getOffice } from "../api/officeandcompany";
import { salaryApi } from "../api/salary";
import { getMyEmployeeProfile } from "../api/employees";
import { useAccess } from "../contexts/AccessContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { resolveClockAction, isPastShiftEnd } from "../utils/clockButtonState";
import { formatHoursMinutes } from "../utils/hoursFormat";
import MonthPolicyStatus from "../components/MonthPolicyStatus";
// Leaving before the shift ends is approval-based when a branch configures
// Settings -> Policy Rules -> "Early clock-out approval". Nothing below renders
// until the backend actually refuses a punch or hands over a request, so an org
// that never switched it on sees the page exactly as before.
import AppealRequestDialog from "../components/attendance/AppealRequestDialog";
import AppealApprovals from "../components/attendance/AppealApprovals";
import MyAppeals from "../components/attendance/MyAppeals";
import EarlyExitRequestDialog from "../components/attendance/EarlyExitRequestDialog";
import EarlyExitApprovals from "../components/attendance/EarlyExitApprovals";
import EarlyExitBanner from "../components/attendance/EarlyExitBanner";

// A punch is only "real" when it has an in- or out-time. The backend can
// return a trailing empty session (both null) for an open / partially-filled
// day; those must never be counted as punches or rendered as a blank row.
const realSessions = (arr?: AttendanceSession[] | null): AttendanceSession[] =>
  (arr || []).filter((s) => !!(s.in_time || s.out_time));

// What the punch dialog's inline forms edit — a new punch being composed, or
// the pending changes to a saved one.
type PunchDraftRow = {
  in_time: string;
  out_time: string;
  location: "in_office" | "work_from_home";
};

// Seed an edit form from a saved punch. Times arrive as HH:MM:SS; the time
// inputs take HH:MM.
const punchDraftFromSession = (s: AttendanceSession): PunchDraftRow => ({
  in_time: s.in_time ? s.in_time.slice(0, 5) : "",
  out_time: s.out_time ? s.out_time.slice(0, 5) : "",
  location: s.location === "work_from_home" ? "work_from_home" : "in_office",
});

// Where a punch came from. Colour-coded because the point of the column is to
// spot the odd one out at a glance — a workflow-made punch sitting among a
// week of website punches is exactly what someone is scanning for. Manual is
// deliberately the loud one: it means a human overrode the record.
const ATTENDANCE_MODE_STYLES: Record<AttendanceMode, string> = {
  website: "bg-sky-100 text-sky-700",
  mobile_app: "bg-indigo-100 text-indigo-700",
  messenger_app: "bg-teal-100 text-teal-700",
  workflow: "bg-slate-200 text-slate-700",
  manual: "bg-amber-100 text-amber-800",
  server: "bg-rose-100 text-rose-700",
};

// The punch list's column layout, named because header, draft rows and saved
// rows must stay in lockstep — three hand-copied template strings is how a
// column ends up misaligned in one of them.
// Columns: # | In | Out | Status | Place | Via
const PUNCH_GRID =
  "grid-cols-[1.5rem_minmax(7rem,1fr)_minmax(7rem,1fr)_6.5rem_minmax(6rem,1fr)_5.5rem]";
// Editable adds a leading checkbox column (bulk edit / delete) and a trailing
// Actions column. Those two extra columns have to be paid for out of the
// others: at the dialog's narrowest the eight columns must still add up to
// less than its inner width, or Actions falls off the right edge where the
// row can't be scrolled to it.
const PUNCH_GRID_EDITABLE =
  "grid-cols-[1.25rem_1.5rem_minmax(7rem,1fr)_minmax(7rem,1fr)_5.5rem_minmax(5rem,1fr)_5.5rem_4.5rem]";

const AttendanceModeBadge = ({
  mode,
  className = "",
}: {
  mode?: AttendanceMode | string | null;
  className?: string;
}) => {
  // Rows written before the backend recorded this — and any value a future
  // client invents — show a dash instead of an unstyled blank chip.
  const known = (mode && mode in ATTENDANCE_MODE_LABELS
    ? (mode as AttendanceMode)
    : null);
  return (
    <span
      title={known ? `Punched from ${ATTENDANCE_MODE_LABELS[known]}` : "Source not recorded"}
      className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 text-center ${
        known ? ATTENDANCE_MODE_STYLES[known] : "text-gray-400"
      } ${className}`}
    >
      {known ? ATTENDANCE_MODE_LABELS[known] : "—"}
    </span>
  );
};

// The out-time to display for a day. Prefer the top-level out_time, but fall
// back to the MOST RECENT punch that actually has a clock-out. The backend
// sets the top-level out_time to null whenever the day ends on an open
// session (still clocked in) — e.g. an office 11:52 → 16:11 punch followed by
// an open 22:12 clock-in. In that case the day's real out-time is still the
// last completed clock-out (16:11), and it should show instead of a blank.
// This also covers the cross-midnight overtime case where the settled
// out-time lives only in `sessions`.
const settledOutTime = (record: AttendanceWorkingHour): string | null => {
  if (record.out_time && record.out_time !== "00:00:00") return record.out_time;
  const punches = realSessions(record.sessions);
  for (let i = punches.length - 1; i >= 0; i--) {
    const out = punches[i].out_time;
    if (out && out !== "00:00:00") return out;
  }
  return null;
};

// Today's date in the user's LOCAL timezone as YYYY-MM-DD. Do NOT use
// `new Date().toISOString()` here — that's UTC, so in IST (UTC+5:30) it returns
// yesterday between midnight and 5:30 AM, which froze the attendance end-date on
// the previous day (and fetched stale data) just after midnight.
const localToday = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Sunday is the company's weekly off. An empty Sunday is dropped from the
// day-wise lists entirely, so any Sunday that DOES appear is a day the employee
// actually worked — worth calling out rather than leaving it to be read as a
// normal weekday.
const isSunday = (raw?: string | null): boolean => {
  const d = (raw || "").slice(0, 10);
  if (!d) return false;
  const dt = new Date(`${d}T00:00:00`);
  return !Number.isNaN(dt.getTime()) && dt.getDay() === 0;
};

const SundayBadge = ({ className = "" }: { className?: string }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200 px-1.5 py-0.5 text-[9px] sm:text-[10px] font-semibold w-fit ${className}`}
    title="Sunday — weekly off. Attendance on this day is weekend work."
  >
    <CalendarDays className="w-2.5 h-2.5" />
    Sunday
  </span>
);

// Declared company holiday (branch holiday calendar). Mirrors SundayBadge; the
// holiday's name goes in the tooltip so "Holiday" stays short in the cell.
const HolidayBadge = ({ name, className = "" }: { name?: string | null; className?: string }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 text-[9px] sm:text-[10px] font-semibold w-fit ${className}`}
    title={name ? `${name} — company holiday` : "Company holiday"}
  >
    <CalendarDays className="w-2.5 h-2.5" />
    Holiday
  </span>
);

const Attendance = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { canManage } = useAccess();
  // Who may override attendance is decided by Access Management alone: the
  // "attendance" panel at Manage. No role is hardcoded here. The backend
  // (`hrms/routers/attendance.py::_authorize_attendance_change`) still applies
  // its own scoping — elevated roles get branch-wide authority, manager-tier is
  // limited to their direct reports — so granting Manage never widens that.
  const canOverrideAttendance = canManage("attendance");
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  // Intern is the employee tier — same self-service attendance view.
  const isEmployee = ["employee", "intern"].includes((user?.role || "").toLowerCase());
  const [myMonthHours, setMyMonthHours] = useState<AttendanceWorkingHour[]>([]);
  // Bumped by actions that change the signed-in user's own attendance rows, so
  // the Attendance Details list below reloads without a page refresh.
  const [myMonthReloadKey, setMyMonthReloadKey] = useState(0);
  // Own joining date, fetched independently so N/A works even with no rows.
  const [myProfileHireDate, setMyProfileHireDate] = useState<string | null>(null);
  const [myMonthLoading, setMyMonthLoading] = useState(false);
  const [pieData, setPieData] = useState<PieData[]>([]);
  const [todayAttendance, setTodayAttendance] = useState<TodayAttendance[]>([]);
  const [employeeStatus, setEmployeeStatus] = useState<AttendanceStatus[]>([]);
  const [filteredEmployeeStatus, setFilteredEmployeeStatus] = useState<AttendanceStatus[]>([]);
  const todayStr = localToday();
  const monthStartStr = todayStr.slice(0, 8) + "01"; // YYYY-MM-01
  const [startDate, setStartDate] = useState<string>(monthStartStr);
  const [endDate, setEndDate] = useState<string>(todayStr);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "Present" | "Absent" | "Leave">("all");
  // Draft filter values bound to the inputs. They only get copied into the
  // applied state (above) when the user clicks Apply, so typing / picking
  // a date doesn't refetch or refilter until they confirm.
  const [draftStartDate, setDraftStartDate] = useState<string>(monthStartStr);
  const [draftEndDate, setDraftEndDate] = useState<string>(todayStr);
  const [draftSearchQuery, setDraftSearchQuery] = useState("");
  const [draftStatusFilter, setDraftStatusFilter] = useState<"all" | "Present" | "Absent" | "Leave">("all");
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  // (filter collapsible state removed — filters are always visible now)
  const [isStatusTableOpen, setIsStatusTableOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // True when the current user's Employee.modeOfWorking is remote or hybrid
  // (or the legacy workfromhome boolean is set). Resolved from the backend
  // on mount via /attendance/my-mode so it's always fresh — even if the
  // localStorage value the JWT-based login wrote is stale or missing.
  // While the request is in flight we leave it false; the clock function
  // still has its own localStorage fallback, so the worst case is the
  // user gets the existing geofenced flow during the first second after
  // page load.
  const [myWorkfromhome, setMyWorkfromhome] = useState(false);
  // Raw Employee.modeOfWorking for the logged-in user. We gate the
  // unlimited clock-in/out behavior on this being exactly "remote" —
  // hybrid / in-office keep the original one-cycle-per-day flow untouched.
  const [myModeOfWorking, setMyModeOfWorking] = useState<string | null>(null);
  // Overtime: live flag + clock-button cutoff (ISO local). When on overtime,
  // the employee gets the SAME unlimited clock-in/out button remote gets,
  // until `myOvertimeUntil` (next working day office start - 30 min).
  const [myIsOvertime, setMyIsOvertime] = useState(false);
  const [myOvertimeUntil, setMyOvertimeUntil] = useState<string | null>(null);
  // Overtime button LOWER bound = work day's office end. The button is only
  // offered AFTER office hours, so it stays hidden until now >= this.
  const [myOvertimeFrom, setMyOvertimeFrom] = useState<string | null>(null);
  // The employee's own shift end ("HH:MM"). In-office Clock In is offered up to
  // this time; after it only overtime re-opens it. null = no bound configured.
  const [myOfficeEnd, setMyOfficeEnd] = useState<string | null>(null);
  // Bumped after a clock in/out so the overtime + shift window re-resolve
  // without a reload (e.g. an admin grants overtime mid-evening).
  const [workModeReloadKey, setWorkModeReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getMyWorkMode()
      .then((m) => {
        if (cancelled) return;
        setMyWorkfromhome(!!m.workfromhome);
        setMyModeOfWorking(m.mode_of_working);
        setMyIsOvertime(!!m.is_overtime);
        setMyOvertimeUntil(m.overtime_clock_until ?? null);
        setMyOvertimeFrom(m.overtime_clock_from ?? null);
        setMyOfficeEnd(m.office_end ?? null);
      })
      .catch(() => { /* silent — sendWebClockEvent has its own fallbacks */ });
    return () => { cancelled = true; };
  }, [workModeReloadKey]);

  // Remote employees may clock in/out any number of times a day; the
  // button below toggles on live session state instead of the once-only
  // first_in / last_out gate the in-office flow uses.
  const isRemote = (myModeOfWorking || "").trim().toLowerCase() === "remote";
  const isHybrid = (myModeOfWorking || "").trim().toLowerCase() === "hybrid";

  // Re-evaluated every 30s so the overtime button auto-hides at the cutoff.
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const isOvertimeActive = !!(
    myIsOvertime &&
    // Overtime button only AFTER office hours: now must be past the "from"
    // (office end). When no "from" is configured, fall back to until-only.
    (!myOvertimeFrom || nowTick >= new Date(myOvertimeFrom).getTime()) &&
    (!myOvertimeUntil || nowTick < new Date(myOvertimeUntil).getTime())
  );

  // Has the employee's shift ended for today? In-office people may clock in and
  // out as often as they like DURING office hours; past the shift end a new
  // Clock In needs overtime, while Clock Out stays available so a punch that is
  // still open (worked till 18:15 on an 18:00 shift) can always be closed.
  const shiftEnded = isPastShiftEnd(myOfficeEnd, nowTick);

  // Remote and overtime ignore the shift window entirely; in-office is gated by
  // `shiftEnded`. Everyone runs off the same live session state below.
  const showSessionClock = isRemote || isOvertimeActive;

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<TodayAttendance | null>(null);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedEmployeeStatus, setSelectedEmployeeStatus] = useState<AttendanceStatus | null>(null);

  // Browser geolocation gate for clocking in. `null` = unknown (the Permissions
  // API isn't available, e.g. older Safari) — we deliberately do NOT gate then,
  // because guessing wrong would hide a Clock In button that actually works.
  // Only an explicit "prompt" / "denied" swaps the button for Allow location.
  const [locationPermission, setLocationPermission] = useState<
    "granted" | "prompt" | "denied" | null
  >(null);
  const [requestingLocation, setRequestingLocation] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) return;
    let status: PermissionStatus | null = null;
    const sync = () => setLocationPermission((status?.state as any) ?? null);
    navigator.permissions
      .query({ name: "geolocation" as PermissionName })
      .then((s) => {
        status = s;
        sync();
        // Granting from the browser's own address-bar control fires this, so the
        // button flips to Clock In without a reload.
        s.addEventListener("change", sync);
      })
      .catch(() => {
        /* unsupported query → stay unknown and leave the clock buttons alone */
      });
    return () => status?.removeEventListener("change", sync);
  }, []);

  // Triggers the browser's native permission prompt. When it was already denied
  // the prompt is suppressed by the browser — the user has to re-allow it from
  // the address bar, which the button's tooltip explains.
  const requestLocationPermission = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    // Over plain HTTP (the app reached by LAN IP, say) the browser refuses
    // location outright and reports it as PERMISSION_DENIED — which would send
    // the user hunting the address bar for a permission toggle that isn't
    // there. Name the real cause before we ever ask.
    if (geolocationBlockedByInsecureOrigin()) {
      toast({
        title: "Location needs a secure connection",
        description:
          "This page is open over plain HTTP, so the browser won't share your location. Open the app over its https:// address and try again.",
        variant: "destructive",
      });
      return;
    }
    setRequestingLocation(true);
    navigator.geolocation.getCurrentPosition(
      () => {
        setLocationPermission("granted");
        setRequestingLocation(false);
      },
      (err) => {
        setRequestingLocation(false);
        setLocationPermission(err.code === err.PERMISSION_DENIED ? "denied" : "granted");
        if (err.code === err.PERMISSION_DENIED) {
          toast({
            title: "Location blocked",
            description:
              "Allow location for this site from the icon in your browser's address bar, then try again.",
            variant: "destructive",
          });
        }
      },
      { timeout: 10000 },
    );
  };

  // Remote: per-day punch breakdown popup (opened by clicking a day row).
  const [sessionsDialogOpen, setSessionsDialogOpen] = useState(false);
  const [sessionsRecord, setSessionsRecord] = useState<AttendanceWorkingHour | null>(null);
  const [sessionsDialogKey, setSessionsDialogKey] = useState(0);
  // Saved punches open for inline editing, keyed by the punch's own record_id
  // → that row's draft. Keying by record_id makes the form open in the SAME row
  // being edited and the save target that punch — editing a multi-punch day
  // used to write to whichever row was newest. Several rows can be open at
  // once ("Edit selected") and saved together ("Save all") or one at a time.
  const [punchDrafts, setPunchDrafts] = useState<Record<string, PunchDraftRow>>({});
  const [savingPunch, setSavingPunch] = useState(false);
  const [deletingPunchIds, setDeletingPunchIds] = useState<string[]>([]);
  // Punches waiting on the delete confirmation — one from a row's trash icon,
  // or every checked row from "Delete selected".
  const [pendingDeletePunchIds, setPendingDeletePunchIds] = useState<string[]>([]);
  // Checked rows in the punch list, for bulk edit / delete.
  const [selectedPunchIds, setSelectedPunchIds] = useState<string[]>([]);
  // NEW punch rows being composed. "Add punch" appends one; several can be
  // queued and saved together ("Save all"), not just one at a time.
  const [newPunchDrafts, setNewPunchDrafts] = useState<PunchDraftRow[]>([]);
  // Chrome's native time picker emits one change for the hour and another for
  // the minute. Track them separately so the first choice never closes it.
  const timePickerChanges = useRef({ in: 0, out: 0 });

  // The open day's punches, and the subsets the bulk controls act on. Only a
  // punch carrying its own record_id can be edited or deleted, so only those
  // get a checkbox; ids that vanished after a refresh are simply not counted.
  const daySessions = realSessions(sessionsRecord?.sessions);
  const selectablePunchIds = daySessions
    .map((s) => s.record_id)
    .filter((id): id is string => !!id);
  const selectedPunches = daySessions.filter(
    (s) => !!s.record_id && selectedPunchIds.includes(s.record_id),
  );
  const editingPunches = daySessions.filter((s) => !!s.record_id && !!punchDrafts[s.record_id]);
  const allPunchesSelected =
    selectablePunchIds.length > 0 && selectablePunchIds.every((id) => selectedPunchIds.includes(id));

  const openPunchEditor = (session: AttendanceSession) => {
    const id = session.record_id;
    if (!id) return;
    // Re-clicking the pencil keeps whatever was already typed in that row.
    setPunchDrafts((drafts) => (drafts[id] ? drafts : { ...drafts, [id]: punchDraftFromSession(session) }));
  };

  const closePunchEditors = (ids: string[]) =>
    setPunchDrafts((drafts) =>
      Object.fromEntries(Object.entries(drafts).filter(([id]) => !ids.includes(id))),
    );

  const openSessionsForRecord = (record: AttendanceWorkingHour) => {
    setPunchDrafts({});
    setSelectedPunchIds([]);
    setNewPunchDrafts([]);
    // Force a clean Radix dialog instance on every row click. After an inline
    // save, the previous nested dialog can disappear while its controlled open
    // state remains true; simply setting true again then does nothing.
    setSessionsDialogOpen(false);
    setSessionsRecord(record);
    setSessionsDialogKey((key) => key + 1);
    requestAnimationFrame(() => setSessionsDialogOpen(true));
  };

  const refreshPunchesForOpenDay = async () => {
    await fetchData(startDate, endDate);
    if (!selectedEmployeeStatus?.user_id || !sessionsRecord) return;
    const refreshed = await getAttendanceDayWise(
      selectedEmployeeStatus.user_id,
      startDate,
      endDate,
    );
    setAttendanceWorkingHours(refreshed);
    const sameDay = refreshed.find((d: AttendanceWorkingHour) => d.date === sessionsRecord.date);
    if (sameDay) setSessionsRecord(sameDay);
    else setSessionsRecord({ ...sessionsRecord, sessions: [] });
  };

  const saveAllNewPunches = async () => {
    if (!canOverrideAttendance || !sessionsRecord || !selectedEmployeeStatus?.user_id) return;
    const drafts = newPunchDrafts;
    if (drafts.length === 0) return;
    if (drafts.some((d) => !d.in_time)) {
      toast({
        title: "Clock-in required",
        description: "Every new punch row needs a clock-in time (remove empty rows with ✕).",
        variant: "destructive",
      });
      return;
    }
    setSavingPunch(true);
    // Save sequentially — the rows share one attendance day, and the backend
    // resolves each add against the day's current punches. Rows that fail
    // stay listed so the admin can retry just those.
    const failed: typeof drafts = [];
    let lastError: any = null;
    for (const d of drafts) {
      try {
        await adminUpdateWorkingHours({
          user_id: selectedEmployeeStatus.user_id,
          attendance_date: sessionsRecord.date,
          clock_in_time: d.in_time,
          clock_out_time: d.out_time || undefined,
          attendance_location: d.location,
          add_new_punch: true,
        });
      } catch (err: any) {
        failed.push(d);
        lastError = err;
      }
    }
    setNewPunchDrafts(failed);
    await refreshPunchesForOpenDay();
    if (failed.length) {
      toast({
        title: `Saved ${drafts.length - failed.length} of ${drafts.length} punches`,
        description: lastError?.message || "The failed rows are still listed — try saving them again.",
        variant: "destructive",
      });
    } else {
      toast({ title: drafts.length > 1 ? `${drafts.length} punches added` : "Punch added" });
    }
    setSavingPunch(false);
  };

  // Save the edited times / location of one punch (the row's own Save) or of
  // every open row ("Save all"). `record_id` pins each update to ITS punch, so
  // a day with several punches never writes to whichever row happens to be
  // newest. Saves run one after another — the rows share one attendance day —
  // and a row that fails stays open for another try.
  const savePunchEdits = async (sessions: AttendanceSession[]) => {
    if (!canOverrideAttendance) {
      toast({
        title: "Permission required",
        description: "You don't have access to edit attendance punches.",
        variant: "destructive",
      });
      return;
    }
    if (!sessionsRecord) return;
    const targets = sessions.filter((s) => !!s.record_id && !!punchDrafts[s.record_id]);
    if (targets.length === 0) return;
    if (targets.some((s) => { const d = punchDrafts[s.record_id!]; return !d.in_time && !d.out_time; })) {
      toast({
        title: "Nothing to save",
        description:
          targets.length > 1
            ? "Every punch being saved needs a clock-in or clock-out time."
            : "Enter a clock-in or clock-out time.",
        variant: "destructive",
      });
      return;
    }
    setSavingPunch(true);
    const failed: string[] = [];
    let lastError: any = null;
    try {
      for (const s of targets) {
        const d = punchDrafts[s.record_id!];
        try {
          await adminUpdateWorkingHours({
            user_id: selectedEmployeeStatus?.user_id,
            attendance_date: sessionsRecord.date,
            clock_in_time: d.in_time || undefined,
            clock_out_time: d.out_time || undefined,
            clear_clock_out: !!s.out_time && !d.out_time,
            attendance_location: d.location,
            record_id: s.record_id,
          });
        } catch (err: any) {
          failed.push(s.record_id!);
          lastError = err;
        }
      }
      const saved = targets.map((s) => s.record_id!).filter((id) => !failed.includes(id));
      closePunchEditors(saved);
      // A saved row's job is done — uncheck it so "Delete selected" can't
      // silently include it afterwards.
      setSelectedPunchIds((ids) => ids.filter((id) => !saved.includes(id)));
      await refreshPunchesForOpenDay();
      if (failed.length) {
        toast({
          title:
            targets.length > 1
              ? `Saved ${saved.length} of ${targets.length} punches`
              : "Couldn't update punch",
          description:
            lastError?.message ||
            (targets.length > 1 ? "The failed rows are still open — try saving them again." : "Please try again."),
          variant: "destructive",
        });
      } else {
        toast({ title: targets.length > 1 ? `${targets.length} punches updated` : "Punch updated" });
      }
    } catch (err: any) {
      toast({
        title: "Couldn't refresh punches",
        description: err?.message || "Please reopen the day to see the saved changes.",
        variant: "destructive",
      });
    } finally {
      setSavingPunch(false);
    }
  };

  // Delete one punch (a row's trash icon) or every checked one ("Delete
  // selected"), after the confirmation dialog. One call per punch — there is
  // no bulk endpoint — so a failure only keeps THAT punch.
  const deletePunches = async (recordIds: string[]) => {
    if (!canOverrideAttendance || !selectedEmployeeStatus?.user_id || recordIds.length === 0) return;
    setDeletingPunchIds(recordIds);
    const failed: string[] = [];
    let lastError: any = null;
    try {
      for (const recordId of recordIds) {
        try {
          await adminDeleteAttendancePunch({
            user_id: selectedEmployeeStatus.user_id,
            record_id: recordId,
          });
        } catch (err: any) {
          failed.push(recordId);
          lastError = err;
        }
      }
      const deleted = recordIds.filter((id) => !failed.includes(id));
      // A deleted punch can't stay open for editing or checked.
      closePunchEditors(deleted);
      setSelectedPunchIds((ids) => ids.filter((id) => !deleted.includes(id)));
      await refreshPunchesForOpenDay();
      if (failed.length) {
        toast({
          title:
            recordIds.length > 1
              ? `Deleted ${deleted.length} of ${recordIds.length} punches`
              : "Couldn't delete punch",
          description:
            lastError?.message ||
            (recordIds.length > 1 ? "The rest are still listed — try again." : "Please try again."),
          variant: "destructive",
        });
      } else {
        toast({ title: recordIds.length > 1 ? `${recordIds.length} punches deleted` : "Punch deleted" });
      }
    } catch (err: any) {
      toast({
        title: "Couldn't refresh punches",
        description: err?.message || "Please reopen the day to see the current punches.",
        variant: "destructive",
      });
    } finally {
      setDeletingPunchIds([]);
    }
  };

  useEffect(() => {
    // The punches dialog is a child of the employee attendance list dialog.
    // Closing the parent must also clear the controlled child; otherwise its
    // portal can mount again after the parent finishes its close animation.
    if (!statusDialogOpen) {
      setSessionsDialogOpen(false);
      setSessionsRecord(null);
      setPunchDrafts({});
      setSelectedPunchIds([]);
      setNewPunchDrafts([]);
      setPendingDeletePunchIds([]);
    }
  }, [statusDialogOpen]);

  const handleSessionsDialogChange = (open: boolean) => {
    setSessionsDialogOpen(open);
    if (open) return;
    setPunchDrafts({});
    setSelectedPunchIds([]);
    setSessionsRecord(null);

    // This popup is opened above another modal. Radix can occasionally leave
    // its body pointer lock behind after the child closes, which blocks the
    // next row click until a hard refresh. Clear only that stale lock after
    // Radix has completed its own close cleanup.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (document.body.style.pointerEvents === "none") {
          document.body.style.pointerEvents = "";
        }
      });
    });
  };
  
  // State for attendance working hours data
  const [attendanceWorkingHours, setAttendanceWorkingHours] = useState<AttendanceWorkingHour[]>([]);
  const [isLoadingWorkingHours, setIsLoadingWorkingHours] = useState(false);
  const [workingHoursError, setWorkingHoursError] = useState<string | null>(null);
  const [hoursModalOpen, setHoursModalOpen] = useState(false);
  const [hoursModalMode, setHoursModalMode] = useState<'mark-present' | 'edit'>('edit');
  const [hoursTarget, setHoursTarget] = useState<AttendanceStatus | null>(null);
  const [hoursInValue, setHoursInValue] = useState("");
  // Optional clock-out — admins can save attendance with only an in-time
  // and add the out-time later, or backfill both for a past date.
  const [hoursOutValue, setHoursOutValue] = useState("");
  const [hoursLocation, setHoursLocation] = useState<"in_office" | "work_from_home">("in_office");
  // EXTRA punch rows composed alongside the main in/out pair, so several
  // punches can be added for the date in one go ("Add N punches").
  const [extraHoursPunches, setExtraHoursPunches] = useState<Array<{
    in_time: string;
    out_time: string;
    location: "in_office" | "work_from_home";
  }>>([]);
  const [hoursDateValue, setHoursDateValue] = useState(
    localToday(),
  );
  const [hoursSubmitting, setHoursSubmitting] = useState(false);
  // Which of the modal's two save actions is in flight — the day's single
  // Mark-Present/Edit row, or an extra appended punch.
  const [hoursSubmitAction, setHoursSubmitAction] = useState<"save" | "punch" | null>(null);
  // A fresh open of the hours modal always starts with no queued extra rows.
  useEffect(() => {
    if (hoursModalOpen) setExtraHoursPunches([]);
  }, [hoursModalOpen]);
  const [markingPresentFor, setMarkingPresentFor] = useState<string | null>(null);
  const [markingAbsentFor, setMarkingAbsentFor] = useState<string | null>(null);
  const [pendingMarkPresent, setPendingMarkPresent] = useState<Set<string>>(
    new Set(),
  );
  const [webClockBusy, setWebClockBusy] = useState<"in" | "out" | null>(null);
  // --- early clock-out approval -------------------------------------------
  // `earlyExitPrompt` is what a REFUSED clock-out told us; `myEarlyExit` is
  // today's own request, so the banner can say where it stands after a reload.
  // `approvalsKey` re-fetches the approver queue once this user has acted.
  const [earlyExitPrompt, setEarlyExitPrompt] = useState<EarlyExitPrompt | null>(null);
  const [earlyExitDialogOpen, setEarlyExitDialogOpen] = useState(false);
  const [myEarlyExit, setMyEarlyExit] = useState<EarlyExitRequestRow | null>(null);
  const [withdrawingEarlyExit, setWithdrawingEarlyExit] = useState(false);
  const [approvalsKey, setApprovalsKey] = useState(0);
  // --- attendance appeals --------------------------------------------------
  // Raised from the button beside Clock In/Out, about a day that is already
  // over. `appealsKey` re-fetches both halves (mine, and the queue I decide)
  // after anything changes either of them.
  const [appealDialogOpen, setAppealDialogOpen] = useState(false);
  const [appealsKey, setAppealsKey] = useState(0);
  // Office-wide shift duration ("HH:MM") computed from the office's
  // working_hours_start / working_hours_end. Used as the fallback when
  // the day-wise API returns no shift info — e.g. an employee with zero
  // Present records in the range. Trustworthy enough as a default since
  // the office working hours apply to everyone in the branch.
  const [officeShiftDuration, setOfficeShiftDuration] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getOffice();
        const office = r?.offices?.[0];
        if (!office) return;
        const start = (office.working_hours_start || "").slice(0, 5);
        const end = (office.working_hours_end || "").slice(0, 5);
        if (!start || !end) return;
        const [sh, sm] = start.split(":").map(Number);
        const [eh, em] = end.split(":").map(Number);
        if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return;
        let mins = eh * 60 + em - (sh * 60 + sm);
        if (mins < 0) mins += 24 * 60; // overnight shifts
        const hh = String(Math.floor(mins / 60)).padStart(2, "0");
        const mm = String(mins % 60).padStart(2, "0");
        if (!cancelled) setOfficeShiftDuration(`${hh}:${mm}`);
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Branch holiday calendar (SalarySettings.holiday_dates, the same list the
  // Leaves → Holidays tab manages): iso date → holiday name. Day-wise rows on
  // these dates render "Holiday" instead of "Absent".
  const [holidaysByDate, setHolidaysByDate] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await salaryApi.getSettings();
        const list: any[] = data?.data?.holiday_dates || [];
        const map: Record<string, string> = {};
        for (const h of list) {
          const iso = (typeof h === "string" ? h : h?.date || "").slice(0, 10);
          if (iso) map[iso] = (typeof h === "string" ? "" : h?.name) || "Holiday";
        }
        if (!cancelled) setHolidaysByDate(map);
      } catch {
        /* non-fatal — days just keep their Present/Absent status */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const holidayNameOf = (raw?: string | null): string | null => {
    const d = (raw || "").slice(0, 10);
    return d ? holidaysByDate[d] ?? null : null;
  };

  // Resolve "my row" from today's status list so the employee card can
  // mirror what the admin sees for them. user_id comes from the decoded
  // JWT (set by AuthContext.loginSelect).
  const myTodayStatus: AttendanceStatus | null = (() => {
    const uid = (user as any)?.user_id || (user as any)?.id;
    if (!uid) return null;
    return employeeStatus.find((e) => e.user_id === uid) || null;
  })();
  const hasClockedIn = !!(
    myTodayStatus?.clock_in_time && myTodayStatus.clock_in_time !== "00:00:00"
  );
  const hasClockedOut = !!(
    myTodayStatus?.out_time && myTodayStatus.out_time !== "00:00:00"
  );
  const isOnLeaveToday = myTodayStatus?.status === "Leave";

  // ── Live "am I currently clocked in?" state ───────────────────────────
  // Each clock-in row is closed by its own clock-out, so the current session
  // state is simply: more in-rows than out-rows today => clocked in. Every work
  // mode needs this now — in-office employees do multiple in/out PAIRS during
  // office hours, so a day-level "has clocked in" flag can't drive the buttons.
  // null = not resolved yet (buttons disabled until the first fetch).
  const [remoteClockedIn, setRemoteClockedIn] = useState<boolean | null>(null);

  const refreshRemoteClockState = useCallback(async () => {
    const uid = (user as any)?.user_id || (user as any)?.id;
    if (!uid) return;
    try {
      const rows = await getAttendanceByDate(todayStr, false);
      const mine = rows.filter((r: any) => r.user_id === uid);
      const inCount = mine.filter((r: any) => r.clock_in_time).length;
      const outCount = mine.filter((r: any) => r.clock_out_time).length;
      setRemoteClockedIn(inCount > outCount);
    } catch {
      // Couldn't read today's punches — hold the last known state instead of
      // asserting "not clocked in", which would offer Clock In to someone who
      // still has a punch open and leave two of them dangling.
    }
  }, [user, todayStr]);

  useEffect(() => {
    if (!isAdmin) refreshRemoteClockState();
  }, [isAdmin, refreshRemoteClockState]);

  // Face check state. `faceStatus === null` means "not asked yet"; the button
  // behaves exactly as before until we know otherwise, so the check can never
  // delay a punch while it is still being resolved.
  const [faceStatus, setFaceStatus] = useState<FaceStatus | null>(null);
  const [faceModal, setFaceModal] = useState<null | "enroll" | "verify">(null);
  const [confirmFaceDelete, setConfirmFaceDelete] = useState(false);
  const [deletingFace, setDeletingFace] = useState(false);

  const deleteFaceData = async () => {
    setDeletingFace(true);
    try {
      const res = await revokeFaceConsent();
      setFaceStatus(await getFaceStatus());
      setConfirmFaceDelete(false);
      toast({
        title: "Face data deleted",
        // Say how many went. "Done" leaves the user trusting a claim they
        // cannot check, which is the wrong note for this particular action.
        description: `${res.templates_deleted} scan${res.templates_deleted === 1 ? "" : "s"} erased. Clock In works as normal.`,
      });
    } catch (err: any) {
      toast({
        title: "Couldn't delete it",
        description: readFaceFailure(err).message,
        variant: "destructive",
      });
    } finally {
      setDeletingFace(false);
    }
  };

  useEffect(() => {
    if (isAdmin) return;
    getFaceStatus()
      .then(setFaceStatus)
      // A failure here means the face layer stays off for this session. Never
      // block attendance on a side-car the user cannot fix.
      .catch(() => setFaceStatus(null));
  }, [isAdmin]);

  const afterClockIn = async () => {
    await refreshRemoteClockState();
    setWorkModeReloadKey((key) => key + 1);
    await fetchData(startDate, endDate);
    setMyMonthReloadKey((key) => key + 1);
  };

  // Today's own early-exit request. One cheap call on mount: without it a
  // pending request disappears from view on any page reload, and the employee
  // has no way to tell whether their ask ever left the building.
  const refreshMyEarlyExit = useCallback(async () => {
    if (isAdmin) return;
    try {
      const { requests } = await listEarlyExitRequests({
        scope: "mine",
        onDate: localToday(),
      });
      setMyEarlyExit(requests[0] || null);
    } catch {
      // The gate may simply not be configured for this branch.
      setMyEarlyExit(null);
    }
  }, [isAdmin]);

  useEffect(() => {
    refreshMyEarlyExit();
  }, [refreshMyEarlyExit]);

  const withdrawEarlyExit = async (request: EarlyExitRequestRow) => {
    setWithdrawingEarlyExit(true);
    try {
      await cancelEarlyExitRequest(request.id);
      setMyEarlyExit(null);
      setApprovalsKey((key) => key + 1);
      toast({ title: "Request withdrawn" });
    } catch (err: any) {
      toast({
        title: "Couldn't withdraw it",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setWithdrawingEarlyExit(false);
    }
  };

  const handleWebClock = async (clockIn: boolean) => {
    // The face check is a layer ABOVE Clock In, and only on the way IN:
    // clocking out never needs to prove who you are, and asking would just add
    // a camera prompt to the end of everyone's day.
    //
    // `available` is what keeps this safe to ship: face-service being down
    // falls straight through to the ordinary punch below. `allowed` behaves
    // identically for someone who hasn't been granted the Face Check-In panel
    // — an ungranted employee clocks in the ordinary way and never learns a
    // camera step exists, which is what a per-person rollout should look like.
    if (clockIn && faceStatus?.enabled && faceStatus.allowed && faceStatus.available) {
      setFaceModal(faceStatus.enrolled ? "verify" : "enroll");
      return;
    }

    setWebClockBusy(clockIn ? "in" : "out");
    try {
      const outcome = await sendWebClockEvent(clockIn, {
        workfromhome: myWorkfromhome,
        overtime: isOvertimeActive,
        hybrid: isHybrid,
      });
      // Refused because leaving early needs approval. Not an error and not a
      // retry: turn the dead end into the request that unblocks it.
      if (!clockIn && outcome.approvalRequired) {
        setEarlyExitPrompt(outcome.approvalRequired);
        setMyEarlyExit(outcome.approvalRequired.pending_request);
        setEarlyExitDialogOpen(true);
        return;
      }
      await refreshRemoteClockState();
      // An overtime grant made after this page loaded should take effect on the
      // next punch, not on the next reload.
      setWorkModeReloadKey((key) => key + 1);
      toast({
        title: clockIn ? "Clocked in" : "Clocked out",
        description: clockIn
          ? "You're marked present for today."
          : "Have a good rest of your day.",
      });
      await fetchData(startDate, endDate);
      // fetchData refreshes the tables above; the Attendance Details list has
      // its own fetch, so nudge it too or it shows stale rows until a reload.
      setMyMonthReloadKey((key) => key + 1);
      // An approval is spent by the punch it authorised — the banner must not
      // keep offering it for the next one.
      if (!clockIn) await refreshMyEarlyExit();
    } catch (err: any) {
      toast({
        title: clockIn ? "Couldn't clock in" : "Couldn't clock out",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setWebClockBusy(null);
    }
  };

  const openHoursModal = (
    emp: AttendanceStatus,
    mode: 'mark-present' | 'edit' = 'edit',
    options?: {
      date?: string;
      inTime?: string;
      outTime?: string;
      attendanceLocation?: "in_office" | "work_from_home" | null;
    },
  ) => {
    setHoursTarget(emp);
    setHoursModalMode(mode);
    const today = localToday();
    setHoursDateValue(options?.date || today);
    setHoursLocation(
      options?.attendanceLocation ||
      emp.attendance_location ||
      "in_office"
    );
    if (mode === 'edit') {
      const initialOut = options?.outTime ?? emp.out_time ?? "";
      setHoursInValue(
        (options?.inTime ?? emp.clock_in_time ?? "").slice(0, 5),
      );
      setHoursOutValue(initialOut.slice(0, 5));
    } else {
      setHoursInValue(options?.inTime ? options.inTime.slice(0, 5) : "");
      setHoursOutValue(options?.outTime ? options.outTime.slice(0, 5) : "");
    }
    setHoursModalOpen(true);
  };

  const openAttendancePunchDialog = async (
    emp: AttendanceStatus,
    date = localToday(),
    startWithAddRow = false,
  ) => {
    if (!emp?.user_id) {
      toast({
        description: "Couldn't identify this employee. Refresh and try again.",
        variant: "destructive",
      });
      return;
    }
    setSelectedEmployeeStatus(emp);
    let record: AttendanceWorkingHour;
    try {
      const days = await getAttendanceDayWise(emp.user_id, date, date);
      record = days.find((day: AttendanceWorkingHour) => day.date === date) || {
        date,
        shift_duration: "",
        In_time: "",
        out_time: "",
        total_working_hours: "",
        total_working_hours_words: "",
        total_hours: 0,
        sessions: [],
      };
    } catch {
      record = {
        date,
        shift_duration: "",
        In_time: "",
        out_time: "",
        total_working_hours: "",
        total_working_hours_words: "",
        total_hours: 0,
        sessions: [],
      };
    }
    openSessionsForRecord(record);
    if (startWithAddRow) {
      setNewPunchDrafts([{ in_time: "", out_time: "", location: "in_office" }]);
    }
  };

  const handleMarkPresent = async (emp: AttendanceStatus, date = localToday()) =>
    openAttendancePunchDialog(emp, date, true);

  const handleEditAttendance = async (emp: AttendanceStatus, date = localToday()) =>
    openAttendancePunchDialog(emp, date, false);

  const [markingAbsentDayFor, setMarkingAbsentDayFor] = useState<string | null>(
    null,
  );
  const handleDayMarkAbsent = async (date: string) => {
    const target = selectedEmployeeStatus;
    if (!target?.user_id) {
      toast({
        description: "Couldn't identify this employee. Refresh and try again.",
        variant: "destructive",
      });
      return;
    }
    setMarkingAbsentDayFor(date);
    try {
      await adminMarkAbsent({
        user_id: target.user_id,
        attendance_date: date,
      });
      toast({
        title: "Marked absent",
        description: `${target.name} is absent on ${date}.`,
      });
      await fetchData(startDate, endDate);
      try {
        const refreshed = await getAttendanceDayWise(
          target.user_id,
          startDate,
          endDate,
        );
        setAttendanceWorkingHours(refreshed);
      } catch {
        /* non-fatal — table refreshes on next reopen */
      }
    } catch (err: any) {
      toast({
        title: "Couldn't mark absent",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setMarkingAbsentDayFor(null);
    }
  };

  const handleMarkAbsent = async (emp: AttendanceStatus) => {
    if (!emp?.user_id) {
      toast({
        description: "Couldn't identify this employee. Refresh and try again.",
        variant: "destructive",
      });
      return;
    }
    setMarkingAbsentFor(emp.user_id);
    try {
      const targetDate = localToday();
      await adminMarkAbsent({
        user_id: emp.user_id,
        attendance_date: targetDate,
      });
      toast({ title: "Marked absent", description: `${emp.name} is now absent.` });
      // Drop any pending-finalize flag so the Mark Present button is
      // re-eligible if the admin wants to flip back.
      setPendingMarkPresent((prev) => {
        if (!prev.has(emp.user_id!)) return prev;
        const next = new Set(prev);
        next.delete(emp.user_id!);
        return next;
      });
      await fetchData(startDate, endDate);
    } catch (err: any) {
      toast({
        title: "Couldn't mark absent",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setMarkingAbsentFor(null);
    }
  };

  // Save the modal's times. `asNewPunch` APPENDS another punch to the day
  // instead of owning that day's single Mark-Present row — for a session the
  // employee never clocked in for (e.g. overtime done without a website
  // clock-in). The appended punch shows in the day's punches popup like any
  // real clock-in/out, so it counts towards hours and overtime.
  const submitHoursOverride = async (asNewPunch = false) => {
    if (!hoursTarget?.user_id) return;
    if (!hoursInValue) {
      toast({
        description: "Please enter a clock-in time.",
        variant: "destructive",
      });
      return;
    }
    if (!hoursDateValue) {
      toast({
        description: "Please pick an attendance date.",
        variant: "destructive",
      });
      return;
    }
    // Clock-out is optional. When provided, enforce a basic in < out
    // sanity check (HH:MM string compare is fine because both come from
    // the same input type and same date).
    if (hoursOutValue && hoursOutValue <= hoursInValue) {
      toast({
        description: "Clock-out time must be after clock-in time.",
        variant: "destructive",
      });
      return;
    }
    // Queued extra punch rows: fully-empty rows are ignored; a filled row
    // needs a clock-in, and its clock-out (when given) must come after.
    const extraRows = extraHoursPunches.filter((p) => p.in_time || p.out_time);
    if (extraRows.some((p) => !p.in_time)) {
      toast({
        description: "Every additional punch row needs a clock-in time.",
        variant: "destructive",
      });
      return;
    }
    if (extraRows.some((p) => p.out_time && p.out_time <= p.in_time)) {
      toast({
        description: "In each additional punch, clock-out must be after clock-in.",
        variant: "destructive",
      });
      return;
    }
    const targetDate = hoursDateValue;
    setHoursSubmitting(true);
    setHoursSubmitAction(asNewPunch ? "punch" : "save");
    try {
      // Step 1: write the clock in (and clock out, if provided) onto
      // the chosen date.
      await adminUpdateWorkingHours({
        user_id: hoursTarget.user_id,
        attendance_date: targetDate,
        clock_in_time: hoursInValue,
        ...(hoursOutValue ? { clock_out_time: hoursOutValue } : {}),
        ...(asNewPunch
          ? { add_new_punch: true }
          : {
              clear_clock_out: hoursModalMode === 'edit' && !hoursOutValue,
              replace_day_record: true,
            }),
        attendance_location: hoursLocation,
      });
      // Queued additional punches ride along with either action, each as an
      // extra session on the same date — sequential, same as the punches
      // dialog's Save all.
      for (const p of extraRows) {
        await adminUpdateWorkingHours({
          user_id: hoursTarget.user_id,
          attendance_date: targetDate,
          clock_in_time: p.in_time,
          ...(p.out_time ? { clock_out_time: p.out_time } : {}),
          add_new_punch: true,
          attendance_location: p.location,
        });
      }
      const totalSaved = 1 + extraRows.length;
      // Step 2: flip status to Present for that date. Past dates are
      // supported by the backend — the admin overrides endpoint accepts
      // any YYYY-MM-DD.
      toast(
        asNewPunch
          ? {
              title: totalSaved > 1 ? `${totalSaved} punches added` : "Punch added",
              description:
                totalSaved > 1
                  ? `${totalSaved} punches added for ${hoursTarget.name} on ${targetDate}.`
                  : hoursOutValue
                    ? `${hoursInValue} – ${hoursOutValue} added for ${hoursTarget.name} on ${targetDate}.`
                    : `${hoursInValue} added for ${hoursTarget.name} on ${targetDate}.`,
            }
          : {
              title: "Marked present",
              description:
                extraRows.length > 0
                  ? `${hoursTarget.name} marked present on ${targetDate} with ${totalSaved} punches.`
                  : hoursOutValue
                    ? `${hoursTarget.name} marked present (${hoursInValue} – ${hoursOutValue}) on ${targetDate}.`
                    : `${hoursTarget.name} marked present at ${hoursInValue} on ${targetDate}.`,
            },
      );

      setHoursModalOpen(false);
      setHoursTarget(null);
      await fetchData(startDate, endDate);
      // The override can target the signed-in user (marking yourself present),
      // and that list fetches separately — keep it in step.
      setMyMonthReloadKey((key) => key + 1);
      // If the per-employee day-wise dialog is open, refresh it too so
      // the edited row reflects the new times immediately.
      if (statusDialogOpen && selectedEmployeeStatus?.user_id) {
        try {
          const refreshed = await getAttendanceDayWise(
            selectedEmployeeStatus.user_id,
            startDate,
            endDate,
          );
          setAttendanceWorkingHours(refreshed);
          // Keep an open punches popup on the same day so a punch added from
          // this modal appears there straight away.
          if (sessionsRecord?.date === targetDate) {
            const sameDay = refreshed.find(
              (d: AttendanceWorkingHour) => d.date === targetDate,
            );
            if (sameDay) setSessionsRecord(sameDay);
          }
        } catch {
          /* non-fatal — the table will refresh on next open */
        }
      }
    } catch (err: any) {
      toast({
        title: asNewPunch ? "Couldn't add punch" : "Couldn't mark present",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setHoursSubmitting(false);
      setHoursSubmitAction(null);
    }
  };

  const fetchData = async (start: string, end: string) => {
    setLoading(true);
    try {
      // Fetch attendance summary for the selected range — drives the
      // pie chart / summary tiles.
      const summaryData = await getAttendanceSummary(start, end);
      setPieData(summaryData);
      // The status endpoint is single-date. When the user has narrowed
      // the range to a single day, snapshot that exact date so the table
      // matches the filter. For multi-day ranges fall back to today.
      const snapshotDate =
        start && end && start === end
          ? start
          : localToday();
      const statusData = await getAttendanceStatus(snapshotDate);
      setEmployeeStatus(statusData);
      setFilteredEmployeeStatus(statusData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData(startDate, endDate);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData(startDate, endDate);
      // Switching dates wipes any "just edited" flags — those only apply
      // to the date the user was looking at when they saved.
      setPendingMarkPresent(new Set());
    }
  }, [startDate, endDate]);

  // Filter employees based on search and status
  useEffect(() => {
    let filtered = [...employeeStatus];

    if (searchQuery) {
      filtered = filtered.filter(emp =>
        emp.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(emp => emp.status === statusFilter);
    }

    setFilteredEmployeeStatus(filtered);
  }, [searchQuery, statusFilter, employeeStatus]);

  const handleEmployeeClick = async (emp: AttendanceStatus) => {
    setSelectedEmployeeStatus(emp);
    setAttendanceWorkingHours([]);
    setWorkingHoursError(null);
    setStatusDialogOpen(true);
    try {
      setIsLoadingWorkingHours(true);
      const workingHoursData = await getAttendanceDayWise(emp.user_id!, startDate, endDate);
      setAttendanceWorkingHours(workingHoursData);
    } catch (error: any) {
      console.error("Error fetching attendance working hours:", error);
      
      setWorkingHoursError(error?.message || "Couldn't load attendance details.");
    } finally {
      setIsLoadingWorkingHours(false);
    }
  };

  const getStatusIcon = (status: string) => {
    if (status === "Present") {
      return <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />;
    }
    if (status === "Leave") {
      return <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600" />;
    }
    if (status === "Holiday") {
      return <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600" />;
    }
    if (status === "N/A") {
      return null; // not joined yet — neutral badge, no icon
    }
    return <XCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />;
  };

  // Shared badge classes per status so Present / Leave / Absent read
  // consistently across the status table, the day-wise dialog and the
  // employee's own attendance list.
  //
  // Use SOLID `bg-{green,amber,red}-100` (not a `bg-gradient-*` tint) on
  // purpose: the Badge's default variant adds `bg-primary`, and a solid
  // background utility is in the same property group, so tailwind-merge drops
  // `bg-primary`. A gradient is a different group (background-image), so it
  // would leave `bg-primary` in place — and the global `.gt-themed` layer
  // rewrites `.bg-primary` to white text via !important, which made the label
  // invisible (white-on-light-tint). With the solid tint the theme instead
  // applies its readable tint+text pairs (--gt-prio-* / --gt-primary-container).
  // The text-/border- utilities are an accessible fallback when un-themed.
  // gt-att-pill--* (defined in global-theme.css) gives all three the same
  // soft-tint + near-black-ink shade as the Present pill, each in its own hue.
  // The bg-*-100 / text-*-800 utilities stay as an un-themed fallback.
  const statusBadgeClass = (status: string) =>
    status === "Present"
      ? "gt-att-pill gt-att-pill--present bg-green-100 text-green-800 border border-green-200"
      : status === "Leave"
        ? "gt-att-pill gt-att-pill--leave bg-amber-100 text-amber-800 border border-amber-200"
        : status === "Holiday"
          ? "gt-att-pill bg-indigo-100 text-indigo-800 border border-indigo-200"
          : status === "N/A"
            ? "gt-att-pill bg-gray-100 text-gray-500 border border-gray-200"
            : "gt-att-pill gt-att-pill--absent bg-red-100 text-red-800 border border-red-200";

  // Small "In Office" / "WFH" chip shown next to the attendance status.
  // Reflects where the day's work happened (both punches at office => In
  // Office; any home end => WFH). Renders nothing when unknown (e.g. an
  // admin Present/Absent mark with no clock-in/out location).
  // Badge for the employee's configured working mode (Employee.modeOfWorking):
  // in-office → In Office, remote → WFH, hybrid → Hybrid. Unknown/empty → none.
  // The Place column: WHICH office the day's punches were actually made at.
  // Deliberately NOT folded into the Status chip above — In Office / WFH is
  // what payroll reads and must keep saying exactly that; the office name is a
  // separate fact, and a multi-office roster needs both side by side. Empty on
  // a remote day and on history written before the backend recorded a place.
  const renderPlace = (officeNames?: string[] | null) => {
    const offices = (officeNames || []).filter(Boolean);
    if (offices.length === 0) {
      return <span className="text-gray-300">—</span>;
    }
    return (
      <span
        title={
          offices.length > 1
            ? `Punched at: ${offices.join(", ")}`
            : `Punched at ${offices[0]}`
        }
        className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-gray-700"
      >
        <Building2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-400 flex-shrink-0" />
        <span className="truncate max-w-[7rem] sm:max-w-[10rem]">
          {/* Several offices in one day: name the first and count the rest, so
              the column cannot stretch the table on a field employee's day. */}
          {offices.length > 1 ? `${offices[0]} +${offices.length - 1}` : offices[0]}
        </span>
      </span>
    );
  };

  const renderWorkLocation = (location?: string | null) => {
    if (!location) return null;
    const m = String(location).trim().toLowerCase();
    let label: string;
    let cls: string;
    let Icon = Building2;
    if (m.includes("remote") || m.includes("home") || m === "wfh") {
      label = "WFH";
      cls = "bg-purple-50 text-purple-700 border-purple-200";
      Icon = Home;
    } else if (m.includes("office") || m.includes("onsite") || m.includes("on-site")) {
      label = "In Office";
      cls = "bg-blue-50 text-blue-700 border-blue-200";
      Icon = Building2;
    } else {
      return null;
    }
    return (
      <span
        title={`Attendance location: ${label}`}
        className={`inline-flex items-center gap-0.5 sm:gap-1 rounded-full px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[11px] font-semibold w-fit border ${cls}`}
      >
        <Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
        {label}
      </span>
    );
  };

  // A day before the viewed employee's joining date isn't an absence — show N/A.
  const beforeJoin = (dateStr?: string, hire?: string | null) =>
    !!hire && !!dateStr && String(dateStr).slice(0, 10) < String(hire).slice(0, 10);

  // Calculate statistics. "Leave" is its own bucket now, so Absent counts
  // only genuinely-absent employees (not those on approved leave).
  const totalEmployees = employeeStatus.length;
  const presentCount = employeeStatus.filter(emp => emp.status === "Present").length;
  const leaveCount = employeeStatus.filter(emp => emp.status === "Leave").length;
  const absentCount = employeeStatus.filter(emp => emp.status === "Absent").length;
  const attendancePercentage = totalEmployees > 0 ? ((presentCount / totalEmployees) * 100).toFixed(1) : 0;

  // Own joining date — independent of attendance rows, so pre-joining N/A
  // works even when the range has zero records.
  useEffect(() => {
    if (isAdmin) return;
    let cancelled = false;
    getMyEmployeeProfile()
      .then((p: any) => { if (!cancelled) setMyProfileHireDate(p?.hire_date || null); })
      .catch(() => { /* fall back to row-derived hire date */ });
    return () => { cancelled = true; };
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    const uid = (user as any)?.id || (user as any)?.user_id;
    if (!uid) return;
    let cancelled = false;
    setMyMonthLoading(true);
    // Follow the applied date-range filter so the employee's Attendance Details
    // list (and the month stats) reflect the selected range. Defaults to the
    // current month (startDate/endDate are seeded to month-start → today).
    getAttendanceDayWise(uid, startDate, endDate)
      .then((rows) => {
        if (!cancelled) setMyMonthHours(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setMyMonthHours([]);
      })
      .finally(() => {
        if (!cancelled) setMyMonthLoading(false);
      });
    return () => { cancelled = true; };
    // isRefreshing lets the Refresh button re-trigger the fetch; myMonthReloadKey
    // lets an action that changes today's row (clock in/out) do the same —
    // fetchData() only refreshes the tables above, not this list.
  }, [user, isAdmin, isRefreshing, startDate, endDate, myMonthReloadKey]);

  // Sundays are the company's weekly off — an EMPTY Sunday shouldn't appear in
  // the day-wise list nor count toward Present/Absent totals. But a Sunday the
  // employee actually WORKED must stay visible (weekend / overtime work), so we
  // only drop the empty ones. (Mirrors the admin per-employee dialog below.)
  const isWorkedRow = (r: AttendanceWorkingHour) =>
    !!(r.In_time && r.In_time !== "00:00:00");
  const myMonthRows = myMonthHours.filter(
    (r) => !isSunday(r.date) || isWorkedRow(r)
  );

  // Joining dates of the viewed employees (self list vs admin-selected dialog).
  // Self prefers the profile (works even with zero attendance rows); admin uses
  // the day-wise rows, which the backend returns per-day with hire_date.
  const myHireDate = myProfileHireDate || myMonthHours.find((r) => r.hire_date)?.hire_date || null;
  // Prefer the joining date carried on the selected employee (from the status
  // list) so pre-joining N/A works even if the day-wise rows omit hire_date;
  // fall back to whatever the day-wise response returned.
  const selectedHireDate =
    selectedEmployeeStatus?.hire_date ||
    attendanceWorkingHours.find((r) => r.hire_date)?.hire_date ||
    null;

  const myMonthStats = (() => {
    // Day-wise rows span the selected date range (one row per day, Sundays
    // excluded), so the row count is the number of working days in range.
    // Days before joining are N/A — excluded from every total. An EMPTY
    // company holiday is a "Holiday" row, not an Absent one, and (like an
    // empty Sunday) doesn't count as a working day; a WORKED holiday counts
    // as Present like any other day.
    const countable = myMonthRows.filter(
      (r) =>
        !beforeJoin(r.date, myHireDate) &&
        !(!isWorkedRow(r) && !r.on_leave && holidayNameOf(r.date))
    );
    const totalDays = countable.length;
    const presentDays = countable.filter(
      (r) => r.In_time && r.In_time !== "00:00:00"
    ).length;
    const leaveDays = countable.filter(
      (r) => !(r.In_time && r.In_time !== "00:00:00") && r.on_leave
    ).length;
    const absentDays = Math.max(0, totalDays - presentDays - leaveDays);
    const presencePct =
      totalDays > 0 ? ((presentDays / totalDays) * 100).toFixed(1) : "0";
    return { totalDays, presentDays, leaveDays, absentDays, presencePct };
  })();

  // Employee Attendance Details list, with the Present/Absent status filter
  // applied (the date range is already handled by the fetch above).
  const myFilteredHours = statusFilter === "all"
    ? myMonthRows
    : myMonthRows.filter((r) => {
        const present = !!(r.In_time && r.In_time !== "00:00:00");
        if (statusFilter === "Present") return present;
        if (statusFilter === "Leave") return !present && !!r.on_leave;
        // "Absent" excludes approved-leave days and company holidays.
        return !present && !r.on_leave && !holidayNameOf(r.date);
      });

  const handleStartDateChange = (date: string) => {
    setDraftStartDate(date);
    if (date > draftEndDate) {
      setDraftEndDate(date);
    }
  };

  const handleEndDateChange = (date: string) => {
    if (date >= draftStartDate) {
      setDraftEndDate(date);
    }
  };

  const applyFilters = () => {
    setSearchQuery(draftSearchQuery);
    setStatusFilter(draftStatusFilter);
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    // Surface the snapshot date whenever the applied range collapses to
    // a single day — the table below will reflect that exact date rather
    // than today, and the popup explains the shift.
    if (draftStartDate && draftEndDate && draftStartDate === draftEndDate) {
      setShowInfoPopup(true);
    }
  };

  const clearAllFilters = () => {
    // Reset to the default range = 1st of this month → today (matching
    // the initial state above).
    const today = localToday();
    const monthStart = today.slice(0, 8) + "01";
    setSearchQuery("");
    setStatusFilter("all");
    setStartDate(monthStart);
    setEndDate(today);
    setDraftSearchQuery("");
    setDraftStatusFilter("all");
    setDraftStartDate(monthStart);
    setDraftEndDate(today);
  };

  const hasPendingFilterChanges =
    draftSearchQuery !== searchQuery ||
    draftStatusFilter !== statusFilter ||
    draftStartDate !== startDate ||
    draftEndDate !== endDate;

  const isFilterActive = (() => {
    const today = localToday();
    const monthStart = today.slice(0, 8) + "01";
    return (
      searchQuery !== "" ||
      statusFilter !== "all" ||
      startDate !== monthStart ||
      endDate !== today
    );
  })();

  // Day-wise rows shown inside the per-employee dialog: the API only
  // returns Present records, but the admin needs to see and act on every
  // workday in the range. We synthesise an Absent row for each missing
  // workday and skip Sundays (the company's weekly off — Saturdays are
  // still business days by default).
  const displayedDayRows: AttendanceWorkingHour[] = (() => {
    if (!startDate || !endDate) return attendanceWorkingHours;
    const byDate = new Map<string, AttendanceWorkingHour>();
    for (const r of attendanceWorkingHours) {
      const d = (r.date || "").slice(0, 10);
      if (d) byDate.set(d, r);
    }
    // The employee's shift_duration is the same across every workday in
    // this dialog (it's their assigned shift). Reuse whatever the API
    // sent on the first present row so synthetic Absent rows show the
    // same expected hours. When the API returned zero present rows we
    // fall back to the office-wide working hours so the column isn't
    // a sea of dashes for employees who haven't clocked in yet.
    const inheritedShift =
      attendanceWorkingHours.find(
        (r) => r.shift_duration && r.shift_duration !== "-",
      )?.shift_duration ||
      officeShiftDuration ||
      "-";
    // Anchor at local midnight to avoid DST / timezone drift when we
    // increment the cursor day by day.
    const start = new Date(startDate + "T00:00:00");
    const end = new Date(endDate + "T00:00:00");
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return attendanceWorkingHours;
    }
    const rows: AttendanceWorkingHour[] = [];
    const cursor = new Date(start);
    const worked = (r?: AttendanceWorkingHour) =>
      !!(r && r.In_time && r.In_time !== "00:00:00");
    while (cursor <= end) {
      const y = cursor.getFullYear();
      const m = String(cursor.getMonth() + 1).padStart(2, "0");
      const d = String(cursor.getDate()).padStart(2, "0");
      const iso = `${y}-${m}-${d}`;
      const existing = byDate.get(iso);
      const sunday = cursor.getDay() === 0;
      // Sundays are the weekly off: never synthesise an empty "Absent" Sunday,
      // but DO show a Sunday the employee actually worked (so weekend / overtime
      // work is visible). Non-Sundays always get a row (present or synth-absent).
      if (!sunday) {
        rows.push(
          existing ?? {
            date: iso,
            shift_duration: inheritedShift,
            In_time: "",
            out_time: "",
            total_working_hours: "00:00",
            total_working_hours_words: "0 minutes",
            total_hours: 0,
          },
        );
      } else if (worked(existing)) {
        rows.push(existing as AttendanceWorkingHour);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return rows;
  })();

  // Summary derived from the merged rows so Absent counts reflect the
  // full date range (minus Sundays), not just the rows the API returned.
  const employeeSummary = (() => {
    // Days before joining are N/A — excluded from every total/average. Empty
    // company holidays are "Holiday" rows (not Absent) and, like Sundays,
    // don't count as working days; a worked holiday counts as Present.
    const countable = displayedDayRows.filter(
      (r) =>
        !beforeJoin(r.date, selectedHireDate) &&
        !(!(r.In_time && r.In_time !== "00:00:00") && !r.on_leave && holidayNameOf(r.date))
    );
    if (countable.length === 0) return null;
    const totalWorkingHours = countable.reduce(
      (sum, record) => sum + (record.total_hours || 0),
      0,
    );
    const totalDays = countable.length;
    const presentDays = countable.filter(
      (r) => r.In_time && r.In_time !== "00:00:00",
    ).length;
    const leaveDays = countable.filter(
      (r) => !(r.In_time && r.In_time !== "00:00:00") && r.on_leave,
    ).length;
    return {
      totalDays,
      presentDays,
      leaveDays,
      absentDays: totalDays - presentDays - leaveDays,
      totalWorkingHours: totalWorkingHours.toFixed(2),
      averageWorkingHours: (totalWorkingHours / totalDays).toFixed(2),
    };
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 sm:h-32 sm:w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        {/* Header Section */}
        <div className="gt-page-hero flex-col md:flex-row gap-4" style={{ alignItems: "flex-start" }}>
          <div>
            <h1 className="gt-page-title">Attendance <em>Management</em></h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Track and manage employee <em className="gt-serif-italic">attendance</em> records.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 min-w-0 w-full md:w-auto">
            {/* Today's Attendance + Clock In/Out — moved up beside the page
                header (employees only). */}
            {!isAdmin && (
              <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden min-w-0 w-full md:w-auto">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
                        <MapPin className="w-3 h-3" /> Today&apos;s Attendance
                        {/* Sunday is the weekly off — say so, so a punch made
                            today reads as deliberate weekend work. Same for a
                            declared company holiday. */}
                        {isSunday(todayStr) && <SundayBadge />}
                        {holidayNameOf(todayStr) && <HolidayBadge name={holidayNameOf(todayStr)} />}
                        {hasClockedIn && renderWorkLocation(myTodayStatus?.attendance_location)}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                          <span className="font-medium text-gray-700">In:</span>
                          <span className="font-mono">
                            {hasClockedIn ? myTodayStatus!.clock_in_time : "—"}
                          </span>
                          {/* An open punch is the one thing on this card that
                              is still changing — say so, quietly. */}
                          {hasClockedIn && !hasClockedOut && (
                            <span
                              className="ml-1 inline-flex items-center gap-1 rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-700"
                              title="You are clocked in right now"
                            >
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
                                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-600" />
                              </span>
                              Live
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5 text-red-400" />
                          <span className="font-medium text-gray-700">Out:</span>
                          <span className="font-mono">
                            {hasClockedOut ? myTodayStatus!.out_time : "—"}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-auto" style={{ width: "auto" }}>
                      {/* Clocking in needs the browser's location. Until it is
                          granted, the punch would fail with a permission error,
                          so ask for it here instead of showing a button that
                          cannot work. Once granted this disappears for good. */}
                      {isOnLeaveToday ? (
                        <Button
                          disabled
                          variant="outline"
                          className="h-9 border-amber-200 bg-amber-50 text-amber-700 disabled:opacity-100 text-xs sm:text-sm cursor-not-allowed"
                          title={myTodayStatus?.leave_type ? `Approved leave: ${myTodayStatus.leave_type}` : "Approved leave today"}
                        >
                          <Briefcase className="w-3.5 h-3.5 mr-1.5" />
                          On Leave
                        </Button>
                      ) : locationPermission === "denied" || locationPermission === "prompt" ? (
                        <Button
                          onClick={requestLocationPermission}
                          disabled={requestingLocation}
                          variant="outline"
                          className="h-9 min-w-[142px] border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-900 focus-visible:ring-emerald-500 text-xs sm:text-sm transition-colors"
                          aria-live="polite"
                          title={
                            locationPermission === "denied"
                              ? "Location is blocked for this site — allow it in your browser's address-bar icon, then press this again"
                              : "Attendance needs your location — allow it to clock in"
                          }
                        >
                          {requestingLocation ? (
                            <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <MapPin className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          {requestingLocation
                            ? "Checking location…"
                            : locationPermission === "denied"
                              ? "Enable location"
                              : "Use my location"}
                        </Button>
                      ) : (
                        // ── Session-based clock buttons ─────────────────────
                        // One rule for every mode, driven by the LIVE session
                        // (open punch or not) rather than a once-per-day flag:
                        //
                        //   clocked in            → Clock Out, always. Someone
                        //                           who works past 18:00 on an
                        //                           18:00 shift must be able to
                        //                           close their punch.
                        //   clocked out, in hours → Clock In again (in-office
                        //                           employees do several
                        //                           in/out PAIRS a day).
                        //   clocked out, after    → nothing … unless overtime is
                        //   the shift end           allowed, which re-opens
                        //                           Clock In (and then Clock Out
                        //                           after it) until the cutoff.
                        //
                        // Remote has no shift bound at all. Buttons stay
                        // disabled until the first state fetch resolves.
                        (() => {
                          const action = resolveClockAction({
                            clockedIn: remoteClockedIn,
                            shiftEnded,
                            unlimited: showSessionClock,
                          });
                          if (action === "clock-out") {
                            return (
                              <Button
                                onClick={() => handleWebClock(false)}
                                disabled={webClockBusy !== null}
                                size="icon"
                                className="h-9 w-9 rounded-full bg-red-600 text-white hover:bg-red-700 shadow-sm transition-transform active:scale-95"
                                title={webClockBusy === "out" ? "Clocking out…" : "Clock out"}
                                aria-label="Clock out"
                              >
                                {webClockBusy === "out" ? (
                                  <RefreshCw className="animate-spin" />
                                ) : (
                                  <LogOut />
                                )}
                              </Button>
                            );
                          }
                          if (action === "clock-in" || action === "loading") {
                            return (
                              <Button
                                onClick={() => handleWebClock(true)}
                                disabled={webClockBusy !== null || action === "loading"}
                                size="icon"
                                className={`h-9 w-9 rounded-full text-white shadow-sm transition-transform active:scale-95 ${
                                  isOvertimeActive
                                    ? "bg-amber-500 hover:bg-amber-600"
                                    : "bg-green-600 hover:bg-green-700"
                                }`}
                                title={
                                  webClockBusy === "in"
                                    ? "Clocking in…"
                                    : isOvertimeActive
                                      ? "Clock in for overtime"
                                      : "Use your current location to clock in"
                                }
                                aria-label={isOvertimeActive ? "Clock in for overtime" : "Clock in"}
                              >
                                {webClockBusy === "in" ? (
                                  <RefreshCw className="animate-spin" />
                                ) : (
                                  <LogIn />
                                )}
                              </Button>
                            );
                          }
                          return (
                            <span
                              className="text-xs sm:text-sm text-gray-500 px-2 py-1"
                              title={
                                myOfficeEnd
                                  ? `Your shift ended at ${myOfficeEnd}. Overtime must be allowed by your admin to clock in again.`
                                  : undefined
                              }
                            >
                              {myOfficeEnd ? `Shift ended (${myOfficeEnd})` : "All done for today"}
                            </span>
                          );
                        })()
                      )}
                    {/* Erasing your own face data has to be reachable from the
                        same place it gets used. The consent screen promises
                        "you can delete it at any time" — a promise kept only in
                        an API is not kept. */}
                    {/* Deliberately NOT gated on `allowed`: somebody who was
                        granted face check-in, enrolled, and then had it revoked
                        still has scans on file, and this is their only way to
                        erase them. Revoking access must not take that away. */}
                    {faceStatus?.enabled && faceStatus.enrolled && (
                      <Button
                        onClick={() => setConfirmFaceDelete(true)}
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition-transform active:scale-95"
                        title={`Delete face data — face check-in is on, ${faceStatus.template_count} scan${
                          faceStatus.template_count === 1 ? "" : "s"
                        } stored. Deleting erases them permanently.`}
                        aria-label="Delete face data"
                      >
                        <ScanFace />
                      </Button>
                    )}
                    {/* Appealing a past day belongs next to the punch buttons:
                        this card is where somebody notices the times are wrong,
                        and sending them to a different screen to say so is how
                        a correction turns into a message to HR instead. */}
                    <Button
                      onClick={() => setAppealDialogOpen(true)}
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-gray-50 transition-transform active:scale-95"
                      title="Appeal Request — ask for a past attendance day to be corrected"
                      aria-label="Appeal request"
                    >
                      <CalendarClock />
                    </Button>
                    </div>
                  </div>
                  {/* Where today's early clock-out request stands. Renders
                      nothing when there isn't one, which is every day for every
                      branch that leaves the rule off. */}
                  <EarlyExitBanner
                    request={myEarlyExit}
                    onWithdraw={withdrawEarlyExit}
                    withdrawing={withdrawingEarlyExit}
                  />
                </CardContent>
              </Card>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="gt-icon-btn sm:hidden"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              {/* <button
                onClick={handleRefresh}
                className="gt-icon-btn"
                disabled={isRefreshing}
                aria-label="Refresh"
              >
                <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button> */}
            </div>
          </div>
        </div>

        {/* Late-coming and monthly-hours position. Self-service only: an admin
            looking at this page is looking at everyone, and the card answers
            "where do I stand?". It renders nothing when neither rule is
            configured for the employee. */}
        {!isAdmin && <MonthPolicyStatus />}

        {/* Early clock-out requests waiting on THIS user. Self-gating: the
            backend decides whose requests these are (routed to you, or your own
            direct reports, or branch-wide for the elevated tiers) and the card
            renders nothing when the queue is empty — so it needs no role check
            of its own here. */}
        <EarlyExitApprovals reloadKey={approvalsKey} />

        {/* Appeals waiting on THIS user (routed to them, their own reports, or
            branch-wide for the elevated tiers). Approving one rewrites the day,
            so the card shows the before and after before it offers the button. */}
        <AppealApprovals
          reloadKey={appealsKey}
          onDecided={() => {
            // The decision changed an attendance record — pull the page's own
            // view of the month back in step with it.
            setAppealsKey((k) => k + 1);
            handleRefresh();
          }}
        />

        {/* Where my own appeals stand. Self-service only: an admin on this page
            is looking at everyone, and this answers "what happened to mine?". */}
        {!isAdmin && <MyAppeals reloadKey={appealsKey} onChanged={() => setAppealsKey((k) => k + 1)} />}

        <AppealRequestDialog
          open={appealDialogOpen}
          onOpenChange={setAppealDialogOpen}
          onResult={(result) => {
            setAppealsKey((k) => k + 1);
            toast({
              title:
                result.status === "updated"
                  ? "Appeal updated"
                  : "Appeal sent",
              description: result.message,
            });
          }}
        />

        <EarlyExitRequestDialog
          open={earlyExitDialogOpen}
          onOpenChange={setEarlyExitDialogOpen}
          prompt={earlyExitPrompt}
          onResult={(result) => {
            setMyEarlyExit(result.request);
            toast({
              title:
                result.status === "not_required"
                  ? "No approval needed"
                  : result.status === "already_approved"
                    ? "Already approved"
                    : "Sent for approval",
              description: result.message,
            });
            // Nothing is in the way any more — finish the clock-out the
            // employee already asked for rather than making them press it again.
            if (result.status === "not_required" || result.status === "already_approved") {
              handleWebClock(false);
            }
          }}
        />

        {isAdmin ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="gt-stat">
              <div className="gt-stat-label">
                <Users className="w-3.5 h-3.5" /> Total Employees
              </div>
              <p className="gt-stat-value">{totalEmployees}</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label">
                <CheckCircle className="w-3.5 h-3.5" /> Present Today
              </div>
              <p className="gt-stat-value" style={{ color: 'var(--gt-accent-strong)' }}>
                {presentCount}
              </p>
            </div>
            <div
              className="gt-stat"
              style={{
                borderColor:
                  absentCount > 0 ? 'rgba(220,38,38,0.18)' : undefined,
              }}
            >
              <div className="gt-stat-label">
                <XCircle className="w-3.5 h-3.5" /> Absent Today
              </div>
              <p
                className="gt-stat-value"
                style={{ color: absentCount > 0 ? '#991b1b' : undefined }}
              >
                {absentCount}
              </p>
              {leaveCount > 0 && (
                <p className="text-[11px] mt-0.5 font-medium text-amber-600 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {leaveCount} on leave
                </p>
              )}
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label">
                <Activity className="w-3.5 h-3.5" /> Attendance Rate
              </div>
              <p className="gt-stat-value">
                {attendancePercentage}
                <span style={{ fontSize: 18, marginLeft: 4, color: 'var(--gt-text-tertiary)' }}>
                  %
                </span>
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="gt-stat">
              <div className="gt-stat-label">
                <CheckCircle className="w-3.5 h-3.5" /> Days Present
                {myMonthLoading && (
                  <span className="text-[10px] text-gray-400 ml-1">loading…</span>
                )}
              </div>
              <p
                className="gt-stat-value"
                style={{ color: 'var(--gt-accent-strong)' }}
              >
                {myMonthStats.presentDays}
                <span
                  style={{
                    fontSize: 14,
                    marginLeft: 6,
                    color: 'var(--gt-text-tertiary)',
                  }}
                >
                  / {myMonthStats.totalDays}
                </span>
              </p>
            </div>
            <div
              className="gt-stat"
              style={{
                borderColor:
                  myMonthStats.absentDays > 0 ? 'rgba(220,38,38,0.18)' : undefined,
              }}
            >
              <div className="gt-stat-label">
                <XCircle className="w-3.5 h-3.5" /> Days Absent
              </div>
              <p
                className="gt-stat-value"
                style={{ color: myMonthStats.absentDays > 0 ? '#991b1b' : undefined }}
              >
                {myMonthStats.absentDays}
              </p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label">
                <TrendingUp className="w-3.5 h-3.5" /> Month Presence
              </div>
              <p className="gt-stat-value">
                {myMonthStats.presencePct}
                <span style={{ fontSize: 18, marginLeft: 4, color: 'var(--gt-text-tertiary)' }}>
                  %
                </span>
              </p>
            </div>
          </div>
        )}

      {/* Filters — flat row of inputs (no Card wrapper). Each field uses
          its label text as a placeholder/title; for <input type="date">,
          which never displays a CSS placeholder, the `title` attr is used
          for accessibility while the input visibly shows the picked date.
          Apply Filters / Clear and the "N of M match" status row sit
          below, also without any card chrome. */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 sm:gap-4">
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 flex-1 ${
              isEmployee ? "lg:grid-cols-3" : "lg:grid-cols-4"
            }`}
          >
          {!isEmployee && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by employee name..."
                value={draftSearchQuery}
                onChange={(e) => setDraftSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && hasPendingFilterChanges) applyFilters();
                }}
                className="w-full pl-9 pr-3 h-10 border border-gray-200 rounded-lg bg-transparent text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          <input
            type="date"
            title="Start Date"
            placeholder="Start Date"
            value={draftStartDate}
            onChange={(e) => handleStartDateChange(e.target.value)}
            className="w-full px-3 h-10 border border-gray-200 rounded-lg bg-transparent text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <input
            type="date"
            title="End Date"
            placeholder="End Date"
            value={draftEndDate}
            onChange={(e) => handleEndDateChange(e.target.value)}
            className="w-full px-3 h-10 border border-gray-200 rounded-lg bg-transparent text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <select
            title="Status Filter"
            value={draftStatusFilter}
            onChange={(e) =>
              setDraftStatusFilter(e.target.value as "all" | "Present" | "Absent" | "Leave")
            }
            className="w-full px-3 h-10 border border-gray-200 rounded-lg bg-transparent text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All status</option>
            <option value="Present">Present</option>
            <option value="Absent">Absent</option>
            <option value="Leave">Leave</option>
          </select>
          </div>

          {/* Apply Filters sits in the same row as the inputs and is always
              visible; Clear only appears once a filter is active. */}
          <div className="flex items-center justify-end gap-2 shrink-0">
            {isFilterActive && (
              <button
                onClick={clearAllFilters}
                className="text-xs sm:text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
              >
                <XCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">Clear</span>
              </button>
            )}
            <Button
              onClick={applyFilters}
              disabled={!hasPendingFilterChanges}
              title={hasPendingFilterChanges ? "Apply filter changes" : "Change a filter to enable Apply"}
              className={`h-10 px-4 text-xs sm:text-sm bg-[#0f2d1f] text-white transition-all ${
                hasPendingFilterChanges
                  ? "opacity-100 shadow-sm hover:bg-[#123b29] hover:shadow-md"
                  : "opacity-35 cursor-not-allowed"
              }`}
            >
              Apply Filters
            </Button>
          </div>
        </div>

        {filteredEmployeeStatus.length !== employeeStatus.length && (
          <div className="mt-2 sm:mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm gap-2">
            <span className="text-blue-700 font-medium">
              <strong>{filteredEmployeeStatus.length}</strong> of <strong>{employeeStatus.length}</strong> employees match
            </span>
            <div className="flex flex-wrap gap-1.5 sm:gap-2 text-xs">
              {searchQuery && (
                <span className="text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs truncate max-w-[150px]">
                  🔍 {searchQuery}
                </span>
              )}
              {statusFilter !== "all" && (
                <span className="text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs">
                  📊 {statusFilter}
                </span>
              )}
              {(startDate !== localToday() || endDate !== localToday()) && (
                <span className="text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs">
                  📅 {startDate.substring(5)} → {endDate.substring(5)}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Employee Status Table (oversight roles only) - Collapsible with horizontal scroll for mobile */}
      {!isEmployee && (
      <Card className="bg-white border-0 shadow-md hover:shadow-lg transition-all mb-4 sm:mb-6 overflow-hidden">
        <div
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsStatusTableOpen(!isStatusTableOpen)}
        >
          <CardHeader className="pb-2 sm:pb-3 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                <CardTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  <span className="hidden sm:inline">Employee Status</span>
                  <span className="sm:hidden">Status</span>
                </CardTitle>
                <CardDescription className="hidden sm:block text-gray-500">
                  {startDate && startDate === endDate && startDate !== localToday()
                    ? "Present / Absent status for the selected date — click a row for the full range"
                    : "Today's Present / Absent status — click a row for the full range"}
                </CardDescription>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">

                <div className="flex items-center gap-1 sm:gap-2 bg-gray-50 rounded-lg px-1.5 sm:px-3 py-0.5 sm:py-1.5">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  <span className="text-[10px] sm:text-sm text-gray-600">
                    {startDate && startDate === endDate && startDate !== localToday()
                      ? startDate.substring(5)
                      : `Today · ${localToday().substring(5)}`}
                  </span>
                </div>
                {isStatusTableOpen ? (
                  <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>
        </div>

        {isStatusTableOpen && (
          <CardContent className="pt-3 sm:pt-4 animate-in slide-in-from-top-2 duration-200">
            <div className="overflow-auto max-h-[70vh] -mx-3 sm:mx-0">
              <div className="min-w-[280px] sm:min-w-full">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">In Time</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Out Time</th>
                      {/* Which office they punched at — separate from Status,
                          which keeps showing In Office / WFH as before. */}
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Place</th>
                      {canOverrideAttendance && (
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-right text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEmployeeStatus?.map((emp, idx) => {
                      const rowKey = emp.user_id || String(idx);
                      // Present but no settled out time => still clocked in.
                      const clockedIn = emp.status === "Present" && !emp.out_time;
                      return (
                      <Fragment key={rowKey}>
                      <tr className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-colors group cursor-pointer" onClick={() => handleEmployeeClick(emp)}>
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <div className="flex items-center gap-1.5 sm:gap-3">
                            <div className="relative w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center shadow-sm flex-shrink-0 overflow-hidden">
                              {/* Initials are the floor, always rendered. The
                                  photo — set in any of the three apps — covers
                                  them, and uncovers them again if the image
                                  404s (a deleted blob, an expired URL). */}
                              <span className="text-[10px] sm:text-xs font-semibold text-gray-700">
                                {emp?.name?.charAt(0).toUpperCase()}
                              </span>
                              {emp?.profile_photo_url && (
                                <img
                                  src={emp.profile_photo_url}
                                  alt=""
                                  loading="lazy"
                                  className="absolute inset-0 w-full h-full object-cover"
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              )}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors text-xs sm:text-sm truncate max-w-[100px] sm:max-w-none">
                                {emp?.name}
                              </span>
                              {emp?.employeeID && (
                                <span className="text-[9px] sm:text-[10px] text-gray-400 font-mono leading-tight truncate max-w-[100px] sm:max-w-[160px]">
                                  {emp.employeeID}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <div className="flex flex-col items-start gap-1">
                            <Badge
                              className={`${statusBadgeClass(emp.status)} font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full flex items-center gap-0.5 sm:gap-1 w-fit shadow-sm text-[10px] sm:text-xs`}
                              title={
                                emp.status === "Leave" && emp.leave_type
                                  ? `On leave: ${emp.leave_type}`
                                  : emp.status === "Holiday" && emp.holiday_name
                                    ? emp.holiday_name
                                    : undefined
                              }
                            >
                              {getStatusIcon(emp.status)}
                              {emp.status === "Present"
                                ? "Present"
                                : emp.status === "Leave"
                                  ? "Leave"
                                  : emp.status === "Holiday"
                                    ? "Holiday"
                                    : emp.status === "N/A"
                                      ? "N/A"
                                      : "Absent"}
                            </Badge>
                            {emp.status === "Present" && renderWorkLocation(emp.attendance_location)}
                          </div>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-gray-600 font-mono text-[10px] sm:text-sm">
                          {emp?.clock_in_time || "-"}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-gray-600 font-mono text-[10px] sm:text-sm">
                          {emp?.out_time ? (
                            <span>{emp.out_time}</span>
                          ) : clockedIn ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-600 px-2 py-0.5 text-[10px] font-sans font-semibold">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> active
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          {renderPlace(emp.office_names)}
                        </td>
                        {canOverrideAttendance && (
                          <td
                            className="py-2 sm:py-3 px-2 sm:px-4 text-right whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {emp.status === "Leave" ? (
                              // On approved leave → no Present/Absent override
                              // from this table; the status is owned by the
                              // leave flow, so the action buttons are hidden.
                              <span className="text-[10px] sm:text-xs text-gray-400 italic">
                                On leave
                              </span>
                            ) : (
                            <div className="inline-flex gap-1.5">
                              {(() => {
                                const alreadyPresent = emp.status === "Present";
                                const inFlight = markingPresentFor === emp.user_id;
                                // Once present, the button morphs into
                                // an Edit action — reopens the modal
                                // pre-filled with today's recorded
                                // clock-in / clock-out so the admin can
                                // tweak either and save again.
                                const title = alreadyPresent
                                  ? "Edit today's clock-in / clock-out time"
                                  : "Set clock-in time and mark this employee present";
                                return (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-[10px] sm:text-xs"
                                    disabled={inFlight}
                                    onClick={() => {
                                      if (!emp?.user_id) {
                                        toast({
                                          description: "Couldn't identify this employee. Refresh and try again.",
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      // Both paths go through the same
                                      // modal; the mode flips so the
                                      // dialog title / labels and the
                                      // prefill behaviour adapt.
                                      if (alreadyPresent) void handleEditAttendance(emp);
                                      else void handleMarkPresent(emp);
                                    }}
                                    title={title}
                                  >
                                    {inFlight
                                      ? "…"
                                      : alreadyPresent
                                        ? "Edit"
                                        : "Mark Present"}
                                  </Button>
                                );
                              })()}
                              
                              {(() => {
                                const alreadyAbsent = emp.status === "Absent";
                                const inFlight = markingAbsentFor === emp.user_id;
                                const disabled = alreadyAbsent || inFlight;
                                const title = alreadyAbsent
                                  ? "Already marked absent"
                                  : "Mark this employee as absent for the selected date";
                                return (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-[10px] sm:text-xs text-red-600 border-red-200 hover:bg-red-50"
                                    disabled={disabled}
                                    onClick={() => handleMarkAbsent(emp)}
                                    title={title}
                                  >
                                    {inFlight ? "…" : "Mark Absent"}
                                  </Button>
                                );
                              })()}

                            </div>
                            )}
                          </td>
                        )}
                      </tr>
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredEmployeeStatus.length === 0 && (
                <div className="text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm">
                  No employees found matching your filters
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>
      )}

      {/* Attendance Details — the logged-in employee's day-wise attendance for
          the current month, shown inline (replaces the today-only Employee
          Status table for employees). */}
      {isEmployee && (
        <Card className="bg-white border-0 shadow-md hover:shadow-lg transition-all mb-4 sm:mb-6 overflow-hidden">
          <CardHeader className="pb-2 sm:pb-3 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                <CardTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  Attendance Details
                </CardTitle>
                <CardDescription className="hidden sm:block text-gray-500">
                  Your day-wise attendance for this month.
                </CardDescription>
              </div>
              {myMonthHours.length > 0 && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
                  <span className="bg-green-100 text-green-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">✅ Present: {myMonthStats.presentDays}</span>
                  {myMonthStats.leaveDays > 0 && (
                    <span className="bg-amber-100 text-amber-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">🌴 Leave: {myMonthStats.leaveDays}</span>
                  )}
                  <span className="bg-red-100 text-red-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">❌ Absent: {myMonthStats.absentDays}</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-3 sm:pt-4">
            {myMonthLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-xs sm:text-sm text-gray-600">Loading attendance data...</span>
              </div>
            ) : myFilteredHours.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                <p className="text-xs sm:text-sm">No attendance records match the selected filters</p>
              </div>
            ) : (
              <div className="overflow-auto max-h-[70vh] -mx-3 sm:mx-0">
                <div className="min-w-[420px] sm:min-w-full">
                  <table className="w-full text-xs sm:text-sm">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Date</th>
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">In Time</th>
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Out Time</th>
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Total Hours</th>
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Status</th>
                        {/* WHICH office the day's punches were made at. Status
                            above keeps showing In Office / WFH unchanged. */}
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Place</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {myFilteredHours.map((record, idx) => {
                        const isPresent = record.In_time && record.In_time !== "00:00:00";
                        const rowHoliday = holidayNameOf(record.date);
                        const rowStatus = beforeJoin(record.date, myHireDate)
                          ? "N/A"
                          : isPresent
                            ? "Present"
                            : record.on_leave
                              ? "Leave"
                              : rowHoliday
                                ? "Holiday"
                                : "Absent";
                        // Same per-day punch breakdown the admin view gets: a
                        // day with >1 real clock-in/out pair (overtime / remote)
                        // becomes a clickable row that opens the SAME shared
                        // punches popup. Data-driven, so a past overtime day
                        // keeps its punches even after overtime is turned off.
                        const sessions = realSessions(record.sessions);
                        const sessionCount = sessions.length;
                        const hasMultiSessions = sessionCount > 1;
                        // Still clocked in → last punch is an open session; show
                        // "active" instead of the previous completed clock-out.
                        const lastSession = sessions[sessionCount - 1];
                        const isActiveSession = !!(lastSession && lastSession.in_time && !lastSession.out_time);
                        return (
                          <tr
                            key={idx}
                            className={`transition-colors ${hasMultiSessions ? 'cursor-pointer hover:bg-green-50' : 'hover:bg-gray-50'}`}
                            onClick={hasMultiSessions ? () => openSessionsForRecord(record) : undefined}
                          >
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-900 text-[10px] sm:text-xs whitespace-nowrap">
                              <div className="flex flex-col gap-0.5">
                                <span>
                                  {new Date(record.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                                {isSunday(record.date) && <SundayBadge />}
                                {rowHoliday && <HolidayBadge name={rowHoliday} />}
                                {hasMultiSessions && (
                                  <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] text-green-700 font-semibold">
                                    <Eye className="w-2.5 h-2.5" />
                                    {sessionCount} punches
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              <span className={`font-mono text-[10px] sm:text-xs ${isPresent ? 'text-green-600' : 'text-gray-400'}`}>
                                {isPresent ? record.In_time : "-"}
                              </span>
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              {isActiveSession ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-600 px-2 py-0.5 text-[10px] font-sans font-semibold">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> active
                                </span>
                              ) : (
                                <span className="font-mono text-[10px] sm:text-xs text-gray-600">
                                  {settledOutTime(record) || "-"}
                                </span>
                              )}
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              <span className="font-semibold text-blue-600 text-[10px] sm:text-xs">{record.total_working_hours}</span>
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              <div className="flex flex-col items-start gap-1">
                                <Badge
                                  className={`${statusBadgeClass(rowStatus)} font-semibold px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full w-fit shadow-sm text-[9px] sm:text-xs`}
                                  title={
                                    rowStatus === "Leave" && record.leave_type
                                      ? `On leave: ${record.leave_type}`
                                      : rowStatus === "Holiday" && rowHoliday
                                        ? rowHoliday
                                        : undefined
                                  }
                                >
                                  {rowStatus}
                                </Badge>
                                {rowStatus === "Present" && renderWorkLocation(record.attendance_location)}
                              </div>
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              {renderPlace(record.office_names)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Logs Dialog - Responsive */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              Attendance Details
            </DialogTitle>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">{selectedRecord?.name} - {selectedRecord?.date}</p>
          </DialogHeader>
          <div className="space-y-2 sm:space-y-3 mt-3 sm:mt-4">
            {selectedRecord ? (
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
                {selectedRecord.in_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Check In</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.in_time}</span>
                  </div>
                )}
                {selectedRecord.additional_out_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Additional Out</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.additional_out_time}</span>
                  </div>
                )}
                {selectedRecord.additional_in_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Additional In</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.additional_in_time}</span>
                  </div>
                )}
                {selectedRecord.end_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Final Out</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.end_time}</span>
                  </div>
                )}
                {selectedRecord.total_time && (
                  <div className="flex justify-between items-center pt-1.5 sm:pt-2">
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">Total Time</span>
                    <span className="text-base sm:text-lg font-bold text-blue-600">{selectedRecord.total_time}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm">No logs available</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Remote per-day punches — every clock-in / clock-out for the day */}
      <Dialog
        key={sessionsDialogKey}
        open={sessionsDialogOpen}
        onOpenChange={handleSessionsDialogChange}
      >
        <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl rounded-2xl sm:p-7">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              Clock-in / Clock-out
              {isSunday(sessionsRecord?.date) && <SundayBadge />}
            </DialogTitle>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {sessionsRecord
                ? new Date(sessionsRecord.date).toLocaleDateString('en-US', {
                    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                  })
                : ""}
              {sessionsRecord
                ? ` · ${daySessions.length} punches`
                : ""}
              {sessionsRecord?.total_working_hours
                ? ` · ${sessionsRecord.total_working_hours} worked`
                : ""}
            </p>
            {canOverrideAttendance && sessionsRecord && (
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                {/* Bulk actions for the checked rows. The slot is always
                    rendered so "Add punch" doesn't jump when a box is ticked. */}
                <div className="flex min-h-[1.75rem] flex-wrap items-center gap-2 text-[11px] text-gray-600">
                  {selectedPunches.length > 0 && (
                    <>
                      <span className="font-semibold">{selectedPunches.length} selected</span>
                      <button
                        type="button"
                        title="Open every checked punch for editing, then save them together"
                        disabled={savingPunch || deletingPunchIds.length > 0}
                        onClick={() => selectedPunches.forEach(openPunchEditor)}
                        className="inline-flex items-center gap-1 rounded border border-blue-600 px-2 py-1 text-[10px] font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                      >
                        <Pencil className="h-3 w-3" /> Edit selected
                      </button>
                      <button
                        type="button"
                        title="Delete every checked punch"
                        disabled={savingPunch || deletingPunchIds.length > 0}
                        onClick={() => setPendingDeletePunchIds(selectedPunches.map((s) => s.record_id!))}
                        className="inline-flex items-center gap-1 rounded border border-red-600 px-2 py-1 text-[10px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-3 w-3" /> Delete selected
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedPunchIds([])}
                        className="text-[10px] text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  title="Add punch — click again to queue several and save them together"
                  aria-label="Add punch"
                  onClick={() => {
                    // Every click queues ANOTHER draft row, so several punches
                    // can be composed and saved in one go ("Save all").
                    setNewPunchDrafts((rows) => [...rows, { in_time: "", out_time: "", location: "in_office" }]);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#0f2d1f] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#173e2c]"
                >
                  <Plus className="h-3.5 w-3.5" /> Add punch
                </button>
              </div>
            )}
          </DialogHeader>
          {sessionsRecord ? (
            <div className="mt-3 sm:mt-4 rounded-xl border border-gray-200 overflow-hidden">
              {/* Column headers — mirror the outer table's In Time / Out Time */}
              <div className={`grid ${canOverrideAttendance ? PUNCH_GRID_EDITABLE : PUNCH_GRID} items-center gap-2 bg-gray-100 px-3 py-2 sm:px-4 text-[9px] sm:text-[11px] font-semibold uppercase tracking-wider text-gray-500`}>
                {canOverrideAttendance && (
                  <input
                    type="checkbox"
                    aria-label="Select all punches"
                    title={allPunchesSelected ? "Unselect all punches" : "Select all punches"}
                    className="h-3.5 w-3.5 cursor-pointer accent-[#0f2d1f] disabled:cursor-default"
                    disabled={selectablePunchIds.length === 0}
                    checked={allPunchesSelected}
                    // Half-ticked while only some rows are checked — a native
                    // checkbox can only show that through the DOM property.
                    ref={(el) => {
                      if (el) el.indeterminate = !allPunchesSelected && selectedPunches.length > 0;
                    }}
                    onChange={(e) => setSelectedPunchIds(e.target.checked ? selectablePunchIds : [])}
                  />
                )}
                <span>#</span>
                <span className="flex items-center gap-1">
                  <LogIn className="w-3 h-3 text-green-600" /> In Time
                </span>
                <span className="flex items-center gap-1">
                  <LogOut className="w-3 h-3 text-red-500" /> Out Time
                </span>
                <span>Location</span>
                {/* WHICH office the punch happened at — separate from Location
                    above, which stays the In Office / WFH status payroll reads. */}
                <span>Place</span>
                {/* Which client the punch came from — website, mobile app,
                    messenger, workflow, or a manual entry by an admin. */}
                <span>Source</span>
                {canOverrideAttendance && <span className="text-right">Actions</span>}
              </div>
              {/* Rows */}
              <div className="max-h-[55vh] overflow-y-auto overflow-x-hidden divide-y divide-gray-100">
                {canOverrideAttendance && newPunchDrafts.map((draft, di) => (
                  <div key={di} className={`grid ${PUNCH_GRID_EDITABLE} items-center gap-2 bg-emerald-50 px-3 py-2.5 sm:px-4 text-xs sm:text-sm`}>
                    {/* Nothing to tick — a row that isn't saved yet has no
                        record to bulk-edit or delete. */}
                    <span />
                    <Plus className="h-4 w-4 text-emerald-700" />
                    <input type="time" value={draft.in_time} onChange={(e) => { const v = e.target.value; setNewPunchDrafts((rows) => rows.map((r, i) => (i === di ? { ...r, in_time: v } : r))); }} className="w-full rounded border border-gray-300 px-1.5 py-1 font-mono text-xs" />
                    <input type="time" value={draft.out_time} onChange={(e) => { const v = e.target.value; setNewPunchDrafts((rows) => rows.map((r, i) => (i === di ? { ...r, out_time: v } : r))); }} className="w-full rounded border border-gray-300 px-1.5 py-1 font-mono text-xs" />
                    <select value={draft.location} onChange={(e) => { const v = e.target.value as "in_office" | "work_from_home"; setNewPunchDrafts((rows) => rows.map((r, i) => (i === di ? { ...r, location: v } : r))); }} className="w-full rounded border border-gray-300 px-1 py-1 text-[10px]">
                      <option value="in_office">In Office</option>
                      <option value="work_from_home">WFH</option>
                    </select>
                    {/* No Place for a hand-typed punch: nobody was located, and
                        picking an office here would claim they were there. */}
                    <span className="text-[9px] text-gray-300">—</span>
                    {/* Not editable: a punch an admin types in IS a manual
                        entry, and letting them label it "website" would put a
                        clock-in the employee never made beyond question. */}
                    <AttendanceModeBadge mode="manual" />
                    <span className="flex items-center justify-end gap-1">
                      <button type="button" title="Remove this row" disabled={savingPunch} onClick={() => setNewPunchDrafts((rows) => rows.filter((_, i) => i !== di))} className="text-[10px] text-gray-500 hover:text-gray-700">✕</button>
                    </span>
                  </div>
                ))}
                {canOverrideAttendance && newPunchDrafts.length > 0 && (
                  <div className="flex items-center justify-end gap-2 bg-emerald-50/60 px-3 py-2 sm:px-4">
                    {/* <button
                      type="button"
                      disabled={savingPunch}
                      onClick={() => setNewPunchDrafts((rows) => [...rows, { in_time: "", out_time: "", location: "in_office" }])}
                      className="inline-flex items-center gap-1 rounded border border-emerald-700 px-2 py-1 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                    >
                      <Plus className="h-3 w-3" /> Add another
                    </button> */}
                    <button
                      type="button"
                      disabled={savingPunch}
                      onClick={saveAllNewPunches}
                      className="rounded bg-[#0f2d1f] px-2.5 py-1 text-[10px] font-semibold text-white hover:bg-[#173e2c] disabled:opacity-50"
                    >
                      {savingPunch
                        ? "Saving…"
                        : newPunchDrafts.length > 1
                          ? `Save all (${newPunchDrafts.length})`
                          : "Save"}
                    </button>
                    <button
                      type="button"
                      disabled={savingPunch}
                      onClick={() => setNewPunchDrafts([])}
                      className="text-[10px] text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  </div>
                )}
                {newPunchDrafts.length === 0 && daySessions.length === 0 && (
                  <div className="py-7 text-center text-xs text-gray-500">No punches for this day</div>
                )}
                {daySessions.map((s, i) => {
                  const active = !s.out_time && !!s.in_time; // open session
                  // Where this punch was CLOSED — a different office, or away
                  // from every office. Null for an open session and for rows
                  // the server auto-closed (no one punched, so no place).
                  const outPlaceLabel = formatPunchPlace(s.out_place);
                  const outIsOffSite =
                    !!s.out_place && !s.out_place.office_name && !s.out_place.work_from_home;
                  const outPlaceMapUrl = punchPlaceMapUrl(s.out_place);
                  const draft = canOverrideAttendance && s.record_id ? punchDrafts[s.record_id] : undefined;
                  const isEditing = !!draft;
                  const isSelected = !!s.record_id && selectedPunchIds.includes(s.record_id);
                  const setDraft = (patch: Partial<PunchDraftRow>) =>
                    setPunchDrafts((drafts) => ({ ...drafts, [s.record_id!]: { ...drafts[s.record_id!], ...patch } }));
                  return (
                    <div
                      key={s.record_id || i}
                      className={`grid ${canOverrideAttendance ? PUNCH_GRID_EDITABLE : PUNCH_GRID} items-center gap-2 px-3 py-2.5 sm:px-4 text-xs sm:text-sm transition-colors ${
                        isEditing
                          ? "bg-blue-50"
                          : isSelected
                          ? "bg-blue-50/60"
                          : active
                          ? "bg-amber-50 hover:bg-amber-100"
                          : "hover:bg-green-50"
                      }`}
                    >
                      {canOverrideAttendance && (
                        s.record_id ? (
                          <input
                            type="checkbox"
                            aria-label={`Select punch ${i + 1}`}
                            className="h-3.5 w-3.5 cursor-pointer accent-[#0f2d1f]"
                            checked={isSelected}
                            onChange={(e) => {
                              const id = s.record_id!;
                              setSelectedPunchIds((ids) =>
                                e.target.checked ? (ids.includes(id) ? ids : [...ids, id]) : ids.filter((x) => x !== id),
                              );
                            }}
                          />
                        ) : (
                          <span />
                        )
                      )}
                      <span className="font-semibold text-gray-400">{i + 1}</span>

                      {/* The edit form replaces the values IN PLACE, so the row
                          being edited is unmistakably the one that will change. */}
                      {isEditing ? (
                        <>
                          <input
                            type="time"
                            value={draft.in_time}
                            onFocus={() => { timePickerChanges.current.in = 0; }}
                            onChange={(e) => {
                              const input = e.currentTarget;
                              setDraft({ in_time: input.value });
                              timePickerChanges.current.in += 1;
                              if (timePickerChanges.current.in >= 2) {
                                requestAnimationFrame(() => input.blur());
                              }
                            }}
                            className="w-full border border-gray-300 rounded px-1.5 py-1 text-xs font-mono"
                          />
                          <input
                            type="time"
                            value={draft.out_time}
                            onFocus={() => { timePickerChanges.current.out = 0; }}
                            onChange={(e) => {
                              const input = e.currentTarget;
                              setDraft({ out_time: input.value });
                              timePickerChanges.current.out += 1;
                              if (timePickerChanges.current.out >= 2) {
                                requestAnimationFrame(() => input.blur());
                              }
                            }}
                            className="w-full border border-gray-300 rounded px-1.5 py-1 text-xs font-mono"
                          />
                          <select
                            value={draft.location}
                            onChange={(e) =>
                              setDraft({ location: e.target.value as "in_office" | "work_from_home" })
                            }
                            className="w-full border border-gray-300 rounded px-1 py-1 text-[10px]"
                          >
                            <option value="in_office">In Office</option>
                            <option value="work_from_home">WFH</option>
                          </select>
                          {/* Read-only during an edit, same reason as Source:
                              the office is where the punch was MADE, and
                              editing its times does not move it. */}
                          <span className="text-[9px] text-gray-500">
                            {s.in_office_name || "—"}
                          </span>
                          {/* Read-only during an edit: the source records where
                              the punch CAME from, which editing its times does
                              not change. */}
                          <AttendanceModeBadge mode={s.attendance_mode} />
                          <span className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              title="Save this punch"
                              disabled={savingPunch}
                              onClick={() => savePunchEdits([s])}
                              className="text-[10px] font-semibold text-white bg-[#0f2d1f] rounded px-1.5 py-1 disabled:opacity-50"
                            >
                              {savingPunch ? "…" : "Save"}
                            </button>
                            <button
                              type="button"
                              title="Discard changes to this punch"
                              disabled={savingPunch}
                              onClick={() => closePunchEditors([s.record_id!])}
                              className="text-[10px] text-gray-500 hover:text-gray-700"
                            >
                              ✕
                            </button>
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="font-mono text-green-700">{s.in_time || "—"}</span>
                          <span className="flex flex-col leading-tight">
                            <span className="font-mono text-red-600 flex items-center gap-2">
                              {s.out_time || (
                                active ? (
                                  <span className="inline-flex items-center gap-1 text-amber-600 text-[10px] sm:text-xs font-semibold">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                    active
                                  </span>
                                ) : (
                                  "—"
                                )
                              )}
                            </span>
                            {/* Which client closed this punch — helps trace an
                                unexpected clock-out back to its source
                                (website / mobile app / messenger / WorkMonitor). */}
                            {s.out_time && s.clock_out_mode && s.clock_out_mode in ATTENDANCE_MODE_LABELS && (
                              <span className="text-[9px] text-gray-400">
                                via {ATTENDANCE_MODE_LABELS[s.clock_out_mode]}
                              </span>
                            )}
                          </span>
                          <span
                            className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 text-center ${
                              s.location === "work_from_home"
                                ? "bg-purple-100 text-purple-700"
                                : s.location === "in_office"
                                ? "bg-emerald-100 text-emerald-700"
                                : "text-gray-400"
                            }`}
                          >
                            {s.location === "work_from_home"
                              ? "WFH"
                              : s.location === "in_office"
                              ? "In Office"
                              : "—"}
                          </span>
                          {/* Place — WHICH office this punch happened at, kept
                              out of the Status chip above on purpose: In Office
                              / WFH is what payroll reads, the office name is
                              extra detail beside it. */}
                          <span className="flex flex-col leading-tight text-[9px]">
                            {s.in_office_name ? (
                              <span className="text-gray-600" title={`Clocked in at ${s.in_office_name}`}>
                                {s.in_office_name}
                              </span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                            {/* The clock-out can be somewhere else entirely —
                                a different office, or off-site. Only worth a
                                second line when it differs from the clock-in. */}
                            {s.out_time && outPlaceLabel && outPlaceLabel !== s.in_office_name && (
                              <span className={outIsOffSite ? "text-amber-600" : "text-gray-500"}>
                                {outIsOffSite ? "⚑ " : "→ "}
                                {outPlaceMapUrl ? (
                                  <a
                                    href={outPlaceMapUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`Open this clock-out's location in Google Maps${
                                      s.out_place?.accuracy ? ` (accurate to ±${s.out_place.accuracy}m)` : ""
                                    }`}
                                    className="underline decoration-dotted"
                                  >
                                    {outPlaceLabel}
                                  </a>
                                ) : (
                                  outPlaceLabel
                                )}
                              </span>
                            )}
                          </span>
                          <AttendanceModeBadge mode={s.attendance_mode} />
                          {canOverrideAttendance && <span className="flex items-center justify-end gap-1">
                            {/* Only a punch that carries its own row id can be
                                edited — older payloads without record_id can't
                                be targeted safely. */}
                            {s.record_id && (
                              <button
                                type="button"
                                title="Edit punch"
                                aria-label="Edit punch"
                                onClick={() => openPunchEditor(s)}
                                className="rounded p-1 text-blue-600 hover:bg-blue-100 hover:text-blue-800"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {s.record_id && (
                              <button
                                type="button"
                                title="Delete punch"
                                aria-label="Delete punch"
                                disabled={deletingPunchIds.includes(s.record_id)}
                                onClick={() => setPendingDeletePunchIds([s.record_id!])}
                                className="rounded p-1 text-red-600 hover:bg-red-100 hover:text-red-800 disabled:opacity-40"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </span>}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Several rows open for editing save together from here. A
                  single open row already has its own Save, so the bar only
                  appears once there is more than one. Sits outside the
                  scrolling list so it stays in reach on a long day. */}
              {canOverrideAttendance && editingPunches.length > 1 && (
                <div className="flex items-center justify-end gap-2 border-t border-blue-100 bg-blue-50/60 px-3 py-2 sm:px-4">
                  <span className="mr-auto text-[11px] text-blue-800">
                    {editingPunches.length} punches being edited
                  </span>
                  <button
                    type="button"
                    disabled={savingPunch}
                    onClick={() => savePunchEdits(editingPunches)}
                    className="rounded bg-[#0f2d1f] px-2.5 py-1 text-[10px] font-semibold text-white hover:bg-[#173e2c] disabled:opacity-50"
                  >
                    {savingPunch ? "Saving…" : `Save all (${editingPunches.length})`}
                  </button>
                  <button
                    type="button"
                    disabled={savingPunch}
                    onClick={() => setPunchDrafts({})}
                    className="text-[10px] text-gray-500 hover:text-gray-700"
                  >
                    Cancel all
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm">
              No punches for this day
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingDeletePunchIds.length > 0} onOpenChange={(open) => !open && setPendingDeletePunchIds([])}>
        <AlertDialogContent className="max-w-sm rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDeletePunchIds.length > 1
                ? `Delete ${pendingDeletePunchIds.length} attendance punches?`
                : "Delete attendance punch?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeletePunchIds.length > 1
                ? "These punches will be permanently removed from the attendance record."
                : "This punch will be permanently removed from the attendance record."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => {
                const ids = pendingDeletePunchIds;
                setPendingDeletePunchIds([]);
                void deletePunches(ids);
              }}
            >
              {pendingDeletePunchIds.length > 1 ? `Delete ${pendingDeletePunchIds.length}` : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Employee Attendance Working Hours Dialog - Responsive with horizontal scroll */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="max-w-[95%] sm:max-w-3xl md:max-w-4xl max-h-[85vh] flex flex-col overflow-hidden rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              Attendance Details - {selectedEmployeeStatus?.name}
              {selectedEmployeeStatus?.employeeID && (
                <span className="text-xs sm:text-sm font-mono font-normal text-gray-400">
                  ({selectedEmployeeStatus.employeeID})
                </span>
              )}
            </DialogTitle>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
              <p className="text-xs sm:text-sm text-gray-500">
                Date Range: {startDate} to {endDate}
              </p>
              {employeeSummary && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
                  <span className="bg-green-100 text-green-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">✅ Present: {employeeSummary.presentDays}</span>
                  {employeeSummary.leaveDays > 0 && (
                    <span className="bg-amber-100 text-amber-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">🌴 Leave: {employeeSummary.leaveDays}</span>
                  )}
                  <span className="bg-red-100 text-red-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">❌ Absent: {employeeSummary.absentDays}</span>
                  <span className="bg-blue-100 text-blue-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">⏱️ Total: {formatHoursMinutes(employeeSummary.totalWorkingHours)}</span>
                </div>
              )}
            </div>
          </DialogHeader>
          
          {isLoadingWorkingHours ? (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-gray-600">Loading attendance data...</span>
            </div>
          ) : workingHoursError ? (
            
            <div className="text-center py-8 sm:py-12">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
                <XCircle className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
              </div>
              <p className="text-sm sm:text-base font-semibold text-red-600 mb-1">
                {workingHoursError}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500">
                Ask an admin to configure office hours for this branch
                so attendance details can be calculated.
              </p>
            </div>
          ) : displayedDayRows.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-gray-500">
              <Clock className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-gray-300 mb-2 sm:mb-3" />
              <p className="text-xs sm:text-sm">No attendance records found for the selected date range</p>
            </div>
          ) : (
            <div className="mt-3 sm:mt-4 flex-1 min-h-0 overflow-auto -mx-4 sm:mx-0">
              <div className="min-w-[500px] sm:min-w-full">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0">
                    <tr>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Date</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Shift</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">In Time</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Out Time</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Total Hours</th>
                      <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Status</th>
                        {/* WHICH office the day's punches were made at. Status
                            above keeps showing In Office / WFH unchanged. */}
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Place</th>
                      {canOverrideAttendance && (
                        <th className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 px-2 sm:px-4 text-right font-semibold text-gray-700 text-[10px] sm:text-xs">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {displayedDayRows.map((record, idx) => {
                      const isPresent = record.In_time && record.In_time !== "00:00:00";
                      const rowHoliday = holidayNameOf(record.date);
                      const rowStatus = beforeJoin(record.date, selectedHireDate)
                        ? "N/A"
                        : isPresent
                          ? "Present"
                          : record.on_leave
                            ? "Leave"
                            : rowHoliday
                              ? "Holiday"
                              : "Absent";
                      // A day's punch breakdown is shown whenever it has more
                      // than one REAL clock-in/out pair. In-office employees do
                      // a single in/out cycle (1 session), so >1 punch
                      // effectively means an overtime / remote day. We gate on
                      // the punch DATA — NOT the live is_overtime / mode flags —
                      // so a PAST overtime day keeps showing its punches even
                      // after the employee's overtime is turned off (which
                      // clears the is_overtime flag + overtime dates).
                      const sessions = realSessions(record.sessions);
                      const sessionCount = sessions.length;
                      // Multi-punch day -> the row is clickable and opens a
                      // popup listing every clock-in / clock-out.
                      const hasMultiSessions = sessionCount > 1;
                      // Still clocked in → last punch is an open session; show
                      // "active" instead of the previous completed clock-out.
                      const lastSession = sessions[sessionCount - 1];
                      const isActiveSession = !!(lastSession && lastSession.in_time && !lastSession.out_time);
                      return (
                        <tr
                          key={idx}
                          className={`transition-colors ${hasMultiSessions ? 'cursor-pointer hover:bg-green-50' : 'hover:bg-gray-50'}`}
                          onClick={hasMultiSessions ? () => openSessionsForRecord(record) : undefined}
                        >
                          <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-900 text-[10px] sm:text-xs whitespace-nowrap">
                            <div className="flex flex-col gap-0.5">
                              <span>
                                {new Date(record.date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              {isSunday(record.date) && <SundayBadge />}
                              {rowHoliday && <HolidayBadge name={rowHoliday} />}
                              {hasMultiSessions && (
                                <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] text-green-700 font-semibold">
                                  <Eye className="w-2.5 h-2.5" />
                                  {sessionCount} punches
                                </span>
                              )}
                            </div>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <div className="flex items-center gap-0.5 sm:gap-1">
                              <Briefcase className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-400" />
                              <span className="text-gray-600 text-[10px] sm:text-xs">{record.shift_duration}</span>
                            </div>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <span className={`font-mono text-[10px] sm:text-xs ${isPresent ? 'text-green-600' : 'text-gray-400'}`}>
                              {record.In_time && record.In_time !== "00:00:00" ? record.In_time : "-"}
                            </span>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            {isActiveSession ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-600 px-2 py-0.5 text-[10px] font-sans font-semibold">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> active
                              </span>
                            ) : (
                              <span className="font-mono text-[10px] sm:text-xs text-gray-600">
                                {settledOutTime(record) || "-"}
                              </span>
                            )}
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <div className="flex flex-col">
                              <span className="font-semibold text-blue-600 text-[10px] sm:text-xs">{record.total_working_hours}</span>
                              <span className="text-[8px] sm:text-[10px] text-gray-400 hidden sm:inline">{record.total_working_hours_words}</span>
                            </div>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <div className="flex flex-col items-start gap-1">
                              <Badge
                                className={`${statusBadgeClass(rowStatus)} font-semibold px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full w-fit shadow-sm text-[9px] sm:text-xs`}
                                title={
                                  rowStatus === "Leave" && record.leave_type
                                    ? `On leave: ${record.leave_type}`
                                    : rowStatus === "Holiday" && rowHoliday
                                      ? rowHoliday
                                      : undefined
                                }
                              >
                                {rowStatus}
                              </Badge>
                              {rowStatus === "Present" && renderWorkLocation(record.attendance_location)}
                            </div>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            {renderPlace(record.office_names)}
                          </td>
                          {canOverrideAttendance && (
                            <td
                              className="py-2 sm:py-3 px-2 sm:px-4 text-right whitespace-nowrap"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {(() => {
                                const rowDate = (record.date || "").slice(0, 10);
                                // Days before the employee joined have no
                                // attendance to edit — show a muted dash
                                // instead of an actionable Edit control.
                                if (beforeJoin(record.date, selectedHireDate)) {
                                  return (
                                    <span className="text-gray-300 text-[10px] sm:text-xs">
                                      —
                                    </span>
                                  );
                                }
                                const absentInFlight =
                                  markingAbsentDayFor === rowDate;
                                return (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-[10px] sm:text-xs"
                                        disabled={
                                          !selectedEmployeeStatus?.user_id ||
                                          absentInFlight
                                        }
                                      >
                                        {absentInFlight ? "…" : "Edit"}
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align="end"
                                      className="bg-white"
                                    >
                                      <DropdownMenuItem
                                        onClick={() => {
                                          if (!selectedEmployeeStatus) return;
                                          if (isPresent) {
                                            void handleEditAttendance(selectedEmployeeStatus, rowDate);
                                          } else {
                                            void handleMarkPresent(selectedEmployeeStatus, rowDate);
                                          }
                                        }}
                                      >
                                        Mark Present
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="text-red-600 focus:text-red-700"
                                        onClick={() =>
                                          handleDayMarkAbsent(rowDate)
                                        }
                                      >
                                        Mark Absent
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                );
                              })()}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={hoursModalOpen} onOpenChange={setHoursModalOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {hoursModalMode === 'mark-present' ? 'Mark Present' : 'Edit Attendance'}
            </DialogTitle>
          </DialogHeader>
          {hoursTarget && (
            <div className="space-y-3">
              <div className="text-xs text-gray-600">
                <strong>{hoursTarget.name}</strong> · {hoursDateValue}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="att-in">
                    Clock In <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="att-in"
                    type="time"
                    value={hoursInValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      const prev = hoursInValue;
                      setHoursInValue(v);
                      if (
                        v.length === 5 &&
                        prev.length === 5 &&
                        v.split(":")[1] !== prev.split(":")[1]
                      ) {
                        setTimeout(() => e.target.blur(), 0);
                      }
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="att-out">
                    Clock Out{" "}
                    <span className="text-[10px] font-normal text-gray-400">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    id="att-out"
                    type="time"
                    value={hoursOutValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      const prev = hoursOutValue;
                      setHoursOutValue(v);
                      if (
                        v.length === 5 &&
                        prev.length === 5 &&
                        v.split(":")[1] !== prev.split(":")[1]
                      ) {
                        setTimeout(() => e.target.blur(), 0);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Work location</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={hoursLocation === "in_office" ? "default" : "outline"}
                    onClick={() => setHoursLocation("in_office")}
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    In Office
                  </Button>
                  <Button
                    type="button"
                    variant={hoursLocation === "work_from_home" ? "default" : "outline"}
                    onClick={() => setHoursLocation("work_from_home")}
                  >
                    <Home className="mr-2 h-4 w-4" />
                    Remote
                  </Button>
                </div>
              </div>
              {canOverrideAttendance && extraHoursPunches.length > 0 && (
                <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50/50 p-2.5">
                  <p className="text-[11px] font-semibold text-emerald-800">
                    Additional punches — saved together with the times above
                  </p>
                  {extraHoursPunches.map((p, pi) => (
                    <div key={pi} className="grid grid-cols-[1fr_1fr_5.5rem_auto] items-center gap-2">
                      <input type="time" value={p.in_time} onChange={(e) => { const v = e.target.value; setExtraHoursPunches((rows) => rows.map((r, i) => (i === pi ? { ...r, in_time: v } : r))); }} className="w-full rounded border border-gray-300 px-1.5 py-1 font-mono text-xs" />
                      <input type="time" value={p.out_time} onChange={(e) => { const v = e.target.value; setExtraHoursPunches((rows) => rows.map((r, i) => (i === pi ? { ...r, out_time: v } : r))); }} className="w-full rounded border border-gray-300 px-1.5 py-1 font-mono text-xs" />
                      <select value={p.location} onChange={(e) => { const v = e.target.value as "in_office" | "work_from_home"; setExtraHoursPunches((rows) => rows.map((r, i) => (i === pi ? { ...r, location: v } : r))); }} className="w-full rounded border border-gray-300 px-1 py-1 text-[10px]">
                        <option value="in_office">In Office</option>
                        <option value="work_from_home">WFH</option>
                      </select>
                      <button type="button" title="Remove this row" disabled={hoursSubmitting} onClick={() => setExtraHoursPunches((rows) => rows.filter((_, i) => i !== pi))} className="text-xs text-gray-500 hover:text-gray-700">✕</button>
                    </div>
                  ))}
                </div>
              )}
              {canOverrideAttendance && (
                <button
                  type="button"
                  disabled={hoursSubmitting}
                  onClick={() => setExtraHoursPunches((rows) => [...rows, { in_time: "", out_time: "", location: hoursLocation }])}
                  className="inline-flex items-center gap-1 rounded border border-emerald-700 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                >
                  <Plus className="h-3 w-3" /> Add another punch row
                </button>
              )}
              <p className="text-[11px] text-gray-500">
                {hoursModalMode === 'mark-present'
                  ? "Enter the clock-in time; the employee will be marked Present. Clock-out can be added now or filled in later."
                  : "Update the clock-in and (optionally) clock-out times for this date."}
              </p>
              {canOverrideAttendance && (
                <p className="text-[11px] text-gray-500">
                  Use <strong>Add punch</strong> to record an EXTRA session for this
                  date instead of overwriting the day — e.g. overtime the employee
                  worked but forgot to clock in for. Queue several with{" "}
                  <strong>Add another punch row</strong> and they all save in one go.
                </p>
              )}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto"
                  disabled={hoursSubmitting}
                  onClick={() => setHoursModalOpen(false)}
                >
                  Cancel
                </Button>
                {canOverrideAttendance && (
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    disabled={hoursSubmitting}
                    onClick={() => submitHoursOverride(true)}
                    title="Add this as an additional punch for the date, keeping existing punches"
                  >
                    {hoursSubmitting && hoursSubmitAction === "punch" ? (
                      "Adding…"
                    ) : (
                      <>
                        <Plus className="mr-1.5 h-4 w-4" />
                        {extraHoursPunches.some((p) => p.in_time || p.out_time)
                          ? `Add ${1 + extraHoursPunches.filter((p) => p.in_time || p.out_time).length} punches`
                          : "Add punch"}
                      </>
                    )}
                  </Button>
                )}
                <Button
                  className="w-full sm:w-auto"
                  disabled={hoursSubmitting}
                  onClick={() => submitHoursOverride(false)}
                >
                  {hoursSubmitting && hoursSubmitAction === "save"
                    ? "Saving…"
                    : hoursModalMode === 'mark-present'
                      ? "Mark Present"
                      : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Info Popup Dialog - Responsive */}
      <Dialog open={showInfoPopup} onOpenChange={setShowInfoPopup}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg text-center">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Information</DialogTitle>
          </DialogHeader>
          <p className="text-xs sm:text-sm text-gray-600 mt-2">
            Below table contains data for{" "}
            {startDate && startDate === endDate
              ? startDate
              : "today's date"}
            .
            <br />
            To see full data click on employee name.
          </p>
          <div className="mt-3 sm:mt-4">
            <Button onClick={() => setShowInfoPopup(false)} className="text-xs sm:text-sm">OK</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmFaceDelete}
        onOpenChange={(open) => !open && setConfirmFaceDelete(false)}
      >
        <AlertDialogContent className="max-w-sm rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your face data?</AlertDialogTitle>
            <AlertDialogDescription>
              Your stored face scans will be erased permanently and your consent
              withdrawn. Clock In keeps working the normal way, and you can set
              face check-in up again whenever you like.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingFace}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); deleteFaceData(); }}
              disabled={deletingFace}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingFace ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FaceCheckModal
        open={faceModal !== null}
        mode={faceModal ?? "verify"}
        status={faceStatus}
        workfromhome={myWorkfromhome}
        hybrid={isHybrid}
        onClose={() => setFaceModal(null)}
        onClockedIn={async () => {
          setFaceModal(null);
          toast({
            title: "Clocked in",
            description: "Face verified — you're marked present for today.",
          });
          await afterClockIn();
        }}
        onEnrolled={async () => {
          // Close and release the camera. Rolling straight into verification
          // left the lens live while the user was already reading the "you're
          // set up" toast and had put the laptop down — a camera that keeps
          // watching after the task is finished is exactly what makes people
          // distrust a feature like this.
          setFaceModal(null);
          try {
            setFaceStatus(await getFaceStatus());
          } catch { /* keep whatever we had */ }
          toast({
            title: "Face check-in is set up",
            description: "Press Clock In again to check in with your face.",
          });
        }}
      />
      </div>
    </div>
  );
};

export default Attendance;
